import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, saveAuthSession, clearAuthSession, physioApi, bookingApi, chatApi, eventsApi, clientNoteApi, adminApi, worksheetApi, blogCategoryApi, getMediaUrl } from '../services/api';
import TherapistNotifications from '../components/TherapistNotifications';
import LiveCollaborativeSheet from '../components/LiveCollaborativeSheet';
import { buildWhatsAppLink } from '../utils/whatsapp';
import InbuiltWhatsAppModal from '../components/InbuiltWhatsAppModal';
import TherapistSlotManager from '../components/TherapistSlotManager';
import TherapistCommunityPostManager from '../components/TherapistCommunityPostManager';
import TherapistAnalyticsView from '../components/TherapistAnalyticsView';
import BookingActionModal from '../components/BookingActionModal';
import '../styles/physio-dashboard.css';

const PHYSIO_TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: '🏠' },
  { key: 'analytics', label: 'Analytics & Insights', icon: '📊' },
  { key: 'profile', label: 'Profile & Availability', icon: '👨‍⚕️' },
  { key: 'community', label: 'Community Posts', icon: '💬' },
  { key: 'worksheets', label: 'Clinical Worksheets', icon: '📑' },
  { key: 'documents', label: 'Documents', icon: '📄' },
  { key: 'bookings', label: 'Bookings', icon: '📅' },
  { key: 'blogs', label: 'Blogs', icon: '✍️' },
  { key: 'notes', label: 'Client Notes', icon: '📋' },
  { key: 'events', label: 'Events/Seminars', icon: '🎉' },
  { key: 'whatsapp', label: 'WhatsApp Chat', icon: '💬' },
];

const toDatetimeLocal = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function TherapistDashboardNew() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  useEffect(() => {
    if (!authSession?.token) {
      navigate('/login', { replace: true });
    }
  }, [authSession?.token, navigate]);

  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [whatsappTarget, setWhatsappTarget] = useState(null);
  const [initialWhatsappMessage, setInitialWhatsappMessage] = useState('');
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    specialization: '',
    education: '',
    shortDescription: '',
    therapyFor: ['Anxiety & Panic', 'Depressive Disorders', 'Stress Management'],
    languages: ['English', 'Hindi'],
    location: '',
    yearsOfExperience: 0,
    membership: 'free',
    bio: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [customTherapyDash, setCustomTherapyDash] = useState('');
  const [customLanguageDash, setCustomLanguageDash] = useState('');

  const defaultTherapyOptionsDash = [
    'Anxiety & Panic',
    'Depressive Disorders',
    'Stress Management',
    'Relationship & Marriage Issues',
    'Trauma & PTSD',
    'Grief & Bereavement',
    'Sleep & Insomnia',
    'OCD & Intrusive Thoughts',
    'Workplace Burnout & Career Stress',
    'Self-Esteem & Confidence',
    'Anger Management',
    'ADHD / Neurodiversity Support'
  ];

  const defaultLanguageOptionsDash = [
    'English',
    'Hindi',
    'Marathi',
    'Bengali',
    'Kannada',
    'Tamil',
    'Telugu',
    'Gujarati',
    'Malayalam',
    'Punjabi',
    'Urdu'
  ];

  const quickEducationSuggestionsDash = [
    'M.A. Clinical Psychology',
    'M.Sc Clinical Psychology',
    'M.Phil Clinical Psychology (RCI)',
    'Ph.D. in Psychology',
    'MBBS, MD (Psychiatry)',
    'PG Diploma in Guidance & Counselling'
  ];

  const toggleTherapyDash = (item) => {
    setProfileData((prev) => {
      const current = Array.isArray(prev.therapyFor) ? prev.therapyFor : [];
      const exists = current.includes(item);
      return {
        ...prev,
        therapyFor: exists ? current.filter((c) => c !== item) : [...current, item],
      };
    });
  };

  const addCustomTherapyDash = (e) => {
    if (e) e.preventDefault();
    const val = customTherapyDash.trim();
    if (val) {
      const current = Array.isArray(profileData.therapyFor) ? profileData.therapyFor : [];
      if (!current.includes(val)) {
        setProfileData((prev) => ({ ...prev, therapyFor: [...current, val] }));
        setCustomTherapyDash('');
      }
    }
  };

  const toggleLanguageDash = (lang) => {
    setProfileData((prev) => {
      const current = Array.isArray(prev.languages) ? prev.languages : [];
      const exists = current.includes(lang);
      if (exists && current.length <= 1) return prev;
      return {
        ...prev,
        languages: exists ? current.filter((l) => l !== lang) : [...current, lang],
      };
    });
  };

  const addCustomLanguageDash = (e) => {
    if (e) e.preventDefault();
    const val = customLanguageDash.trim();
    if (val) {
      const current = Array.isArray(profileData.languages) ? profileData.languages : [];
      if (!current.includes(val)) {
        setProfileData((prev) => ({ ...prev, languages: [...current, val] }));
        setCustomLanguageDash('');
      }
    }
  };

  const [activeTab, setActiveTab] = useState('dashboard');

  // Blogs state & API integration (Matching Admin Blog Form)
  const [blogs, setBlogs] = useState([]);
  const [blogCategories, setBlogCategories] = useState([
    'Mental Health',
    'Anxiety & Stress',
    'Depression',
    'Relationships',
    'Self-Care',
    'Mindfulness',
    'Career & Work',
    'Personal Growth'
  ]);
  const [blogForm, setBlogForm] = useState({
    title: '',
    author: authSession?.user?.name ? `Dr. ${authSession.user.name}` : '',
    category: 'Mental Health',
    excerpt: '',
    content: '',
    featuredImage: ''
  });
  const [editBlogId, setEditBlogId] = useState(null);
  const [editBlogForm, setEditBlogForm] = useState({
    title: '',
    author: '',
    category: 'Mental Health',
    excerpt: '',
    content: '',
    featuredImage: ''
  });
  const [uploadingBlogImage, setUploadingBlogImage] = useState(false);
  const [uploadingEditBlogImage, setUploadingEditBlogImage] = useState(false);

  // Fetch blogs & categories from API
  useEffect(() => {
    if (activeTab === 'blogs' && authSession?.token) {
      physioApi.getBlogs()
        .then(data => setBlogs(data.blogs || data || []))
        .catch(err => {
          console.error('Blogs error:', err);
          setErrorMessage(err.message || 'Failed to load blogs');
        });

      blogCategoryApi.getPublicCategories()
        .then(res => {
          if (res?.categories && Array.isArray(res.categories) && res.categories.length > 0) {
            setBlogCategories(res.categories.map(c => c.name));
          }
        })
        .catch(err => console.warn('Could not load blog categories:', err));
    }
  }, [activeTab]);

  // Worksheets state & API integration
  const [worksheets, setWorksheets] = useState([]);
  const [worksheetSearch, setWorksheetSearch] = useState('');
  const [worksheetCategory, setWorksheetCategory] = useState('all');
  const [downloadingWorksheetId, setDownloadingWorksheetId] = useState(null);
  const [selectedDashboardWorksheet, setSelectedDashboardWorksheet] = useState(null);

  // Fetch worksheets from API
  useEffect(() => {
    if (activeTab === 'worksheets' && authSession?.token) {
      worksheetApi.therapistGetWorksheets()
        .then(data => setWorksheets(data.worksheets || []))
        .catch(err => {
          console.error('Worksheets error:', err);
          setErrorMessage(err.message || 'Failed to load worksheets');
        });
    }
  }, [activeTab]);

  const handleDashboardWorksheetDownload = async (w) => {
    const id = w._id || w.id;
    try {
      setDownloadingWorksheetId(id);
      const res = await worksheetApi.therapistDownloadWorksheet(id);
      const targetUrl = res?.downloadUrl || w.fileUrl;
      const downloadLink = document.createElement('a');
      downloadLink.href = targetUrl;
      downloadLink.target = '_blank';
      downloadLink.download = res?.fileName || w.fileName || 'worksheet.pdf';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      setWorksheets(prev => prev.map(item => ((item._id === id || item.id === id) ? { ...item, downloadCount: (item.downloadCount || 0) + 1 } : item)));
    } catch (err) {
      window.open(w.fileUrl, '_blank');
    } finally {
      setDownloadingWorksheetId(null);
    }
  };

  // Notes & Collaborative Worksheets state & API integration
  const [notes, setNotes] = useState([]);
  const [availableWorksheets, setAvailableWorksheets] = useState([]);
  const [selectedLiveSheetNote, setSelectedLiveSheetNote] = useState(null);

  const initialNoteFormState = {
    clientName: '',
    clientId: '',
    clientEmail: '',
    note: '',
    selectedWorksheetId: '',
  };

  const [noteForm, setNoteForm] = useState(initialNoteFormState);
  const [editNoteId, setEditNoteId] = useState(null);
  const [editNoteForm, setEditNoteForm] = useState(initialNoteFormState);

  // Fetch notes and available worksheets from API
  useEffect(() => {
    if (activeTab === 'notes' && authSession?.token) {
      clientNoteApi.getNotes()
        .then(data => setNotes(data.notes || data || []))
        .catch(err => {
          console.error('Notes error:', err);
          setErrorMessage(err.message || 'Failed to load notes');
        });

      worksheetApi.therapistGetWorksheets()
        .then(res => {
          if (res?.worksheets) {
            setAvailableWorksheets(res.worksheets);
          }
        })
        .catch(err => console.error('Failed to load worksheets library:', err));
    }
  }, [activeTab]);

  // Events state & API integration
  const [events, setEvents] = useState([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [uploadingEventVideo, setUploadingEventVideo] = useState(false);
  const [eventVideoError, setEventVideoError] = useState('');

  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    eventType: 'seminar',
    mode: 'online',
    publishWeeks: 1,
    publishPlanId: '',
    publishPrice: 499,
    scheduledAt: '',
    endsAt: '',
    location: '',
    eventFee: 'Free',
    registrationLink: '',
    videoUrl: '',
    videoFileName: '',
    videoFileSize: 0,
  });
  const [editEventId, setEditEventId] = useState(null);
  const [editEventForm, setEditEventForm] = useState({
    title: '',
    description: '',
    eventType: 'seminar',
    mode: 'online',
    publishWeeks: 1,
    publishPlanId: '',
    publishPrice: 499,
    scheduledAt: '',
    endsAt: '',
    location: '',
    eventFee: 'Free',
    registrationLink: '',
    videoUrl: '',
    videoFileName: '',
    videoFileSize: 0,
  });
  const [eventEligibility, setEventEligibility] = useState({
    isPro: false,
    isFirstFreeEligible: false,
    plans: [],
  });
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [isProcessingEvent, setIsProcessingEvent] = useState(false);

  // Extend / Republish Modals State
  const [extendModal, setExtendModal] = useState({
    isOpen: false,
    event: null,
    planId: '',
    isProcessing: false,
  });
  const [republishModal, setRepublishModal] = useState({
    isOpen: false,
    event: null,
    planId: '',
    scheduledAt: '',
    endsAt: '',
    isProcessing: false,
  });

  // Razorpay SDK loader
  const loadRazorpaySDK = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const formatExpiryDisplay = (event) => {
    if (!event) return null;
    const expiryDate = event.publishExpiresAt ? new Date(event.publishExpiresAt) : (event.endsAt ? new Date(event.endsAt) : null);
    if (!expiryDate) return null;
    const now = new Date();
    const isExpired = expiryDate < now;
    const diffMs = expiryDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    return {
      isExpired,
      formattedDate: expiryDate.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }),
      diffDays,
      text: isExpired ? `Expired on ${expiryDate.toLocaleDateString([], { month: 'short', day: 'numeric' })}` : `${diffDays} Day${diffDays === 1 ? '' : 's'} Remaining (Active till ${expiryDate.toLocaleDateString([], { month: 'short', day: 'numeric' })})`,
    };
  };

  // Video Upload Handler for Therapist Dashboard
  const handleEventVideoUpload = async (e, isEdit = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingEventVideo(true);
      setEventVideoError('');

      const formData = new FormData();
      formData.append('video', file);

      const res = await adminApi.uploadEventVideo(formData);
      if (res?.url) {
        if (isEdit) {
          setEditEventForm((prev) => ({
            ...prev,
            videoUrl: res.url,
            videoFileName: res.fileName || file.name,
            videoFileSize: res.fileSize || file.size,
          }));
        } else {
          setEventForm((prev) => ({
            ...prev,
            videoUrl: res.url,
            videoFileName: res.fileName || file.name,
            videoFileSize: res.fileSize || file.size,
          }));
        }
      } else {
        throw new Error(res?.message || 'Failed to upload video');
      }
    } catch (err) {
      console.error('Video upload error:', err);
      setEventVideoError(err.message || 'Failed to upload video file');
    } finally {
      setUploadingEventVideo(false);
      e.target.value = '';
    }
  };

  const handleRemoveEventVideo = (isEdit = false) => {
    if (isEdit) {
      setEditEventForm((prev) => ({
        ...prev,
        videoUrl: '',
        videoFileName: '',
        videoFileSize: 0,
      }));
    } else {
      setEventForm((prev) => ({
        ...prev,
        videoUrl: '',
        videoFileName: '',
        videoFileSize: 0,
      }));
    }
  };

  const calculateEventEndDate = (startStr, weeks) => {
    if (!startStr) return '';
    try {
      const start = new Date(startStr);
      if (isNaN(start.getTime())) return '';
      const w = Math.max(1, parseInt(weeks, 10) || 1);
      const end = new Date(start.getTime() + w * 7 * 24 * 60 * 60 * 1000);
      return toDatetimeLocal(end.toISOString());
    } catch {
      return '';
    }
  };

  // Fetch events & eligibility from API
  const fetchEventsAndEligibility = async () => {
    if (!authSession?.token) return;
    try {
      const [eventsRes, eligRes] = await Promise.all([
        eventsApi.getMyEvents(),
        eventsApi.getEligibility(),
      ]);
      setEvents(eventsRes.events || eventsRes || []);
      setEventEligibility(eligRes || {});
      if (eligRes?.plans?.length > 0 && !selectedPlanId) {
        setSelectedPlanId(eligRes.plans[0]._id);
      }
    } catch (err) {
      console.error('Events error:', err);
      setErrorMessage(err.message || 'Failed to load events');
    }
  };

  useEffect(() => {
    if (activeTab === 'events' && authSession?.token) {
      fetchEventsAndEligibility();
    }
  }, [activeTab]);

  // WhatsApp Chat state & API integration
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [chatSearch, setChatSearch] = useState('');
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  // Fetch chats from API
  useEffect(() => {
    if (activeTab === 'whatsapp' && authSession?.token) {
      chatApi.getChats()
        .then(data => setChats(data.chats || data || []))
        .catch(err => {
          console.error('Chats error:', err);
          setErrorMessage(err.message || 'Failed to load chats');
        });
    }
  }, [activeTab]);

  // Fetch messages when a chat is selected
  useEffect(() => {
    if (selectedChat && selectedChat._id) {
      setIsLoadingMessages(true);
      chatApi.getMessages(selectedChat._id)
        .then(data => setMessages(data.messages || data || []))
        .catch(err => {
          console.error('Messages error:', err);
          setErrorMessage(err.message || 'Failed to load messages');
        })
        .finally(() => setIsLoadingMessages(false));
    } else {
      setMessages([]);
    }
  }, [selectedChat]);

  const [documentData, setDocumentData] = useState({
    documentType: 'license',
    fileUrl: '',
    originalName: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [user, setUser] = useState(authSession?.user || null);
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingActionModalOpen, setBookingActionModalOpen] = useState(false);
  const [bookingActionModalTarget, setBookingActionModalTarget] = useState(null);
  const [bookingActionModalType, setBookingActionModalType] = useState('reschedule');
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const [profileResponse, bookingResponse] = await Promise.all([
          physioApi.me(),
          bookingApi.mine(),
        ]);
        setProfile(profileResponse.profile || {});
        setDocuments(profileResponse.documents || []);
        setBookings(bookingResponse.bookings || []);
        if (profileResponse.user) {
          setUser(profileResponse.user);
        }
        setProfileData({
          name: profileResponse.user?.name || authSession?.user?.name || '',
          email: profileResponse.user?.email || authSession?.user?.email || '',
          phone: profileResponse.user?.phone || authSession?.user?.phone || '',
          specialization: profileResponse.profile?.specialization || profileResponse.user?.specialization || '',
          education: profileResponse.profile?.education || profileResponse.user?.education || '',
          shortDescription: profileResponse.profile?.shortDescription || profileResponse.user?.shortDescription || '',
          therapyFor: (profileResponse.profile?.therapyFor && profileResponse.profile?.therapyFor.length > 0)
            ? profileResponse.profile.therapyFor
            : (profileResponse.user?.therapyFor && profileResponse.user?.therapyFor.length > 0)
            ? profileResponse.user.therapyFor
            : ['Anxiety & Panic', 'Depressive Disorders', 'Stress Management'],
          languages: (profileResponse.profile?.languages && profileResponse.profile?.languages.length > 0)
            ? profileResponse.profile.languages
            : (profileResponse.user?.languages && profileResponse.user?.languages.length > 0)
            ? profileResponse.user.languages
            : ['English', 'Hindi'],
          location: profileResponse.profile?.location || profileResponse.user?.location || '',
          yearsOfExperience: profileResponse.profile?.yearsOfExperience !== undefined ? profileResponse.profile?.yearsOfExperience : (profileResponse.user?.yearsOfExperience || 0),
          membership: profileResponse.profile?.membership || profileResponse.user?.membership || 'free',
          bio: profileResponse.profile?.bio || profileResponse.user?.bio || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } catch (error) {
        console.error('Dashboard load error:', error);
        setErrorMessage(error.message || 'Unable to load dashboard.');
      }
    };

    if (authSession?.token) {
      loadDashboard();
    } else {
      setErrorMessage('Please login as a mental health practitioner first.');
    }
  }, []);

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (profileData.newPassword) {
      if (profileData.newPassword.length < 6) {
        setErrorMessage('New password must be at least 6 characters long.');
        return;
      }
      if (profileData.newPassword !== profileData.confirmPassword) {
        setErrorMessage('New password and confirm password do not match.');
        return;
      }
      if (!profileData.currentPassword) {
        setErrorMessage('Current password is required to set a new password.');
        return;
      }
    }

    try {
      const payload = {
        name: profileData.name,
        email: profileData.email,
        phone: profileData.phone,
        specialization: profileData.specialization,
        education: profileData.education,
        shortDescription: profileData.shortDescription,
        therapyFor: profileData.therapyFor,
        languages: profileData.languages,
        location: profileData.location,
        yearsOfExperience: profileData.yearsOfExperience,
        bio: profileData.bio,
      };
      if (profileData.newPassword) {
        payload.currentPassword = profileData.currentPassword;
        payload.newPassword = profileData.newPassword;
      }

      const response = await physioApi.updateMyProfile(payload);
      if (response.user) {
        setUser(response.user);
        setProfileData(prev => ({
          ...prev,
          name: response.user.name || prev.name,
          email: response.user.email || prev.email,
          phone: response.user.phone !== undefined ? response.user.phone : prev.phone,
          specialization: response.user.specialization || prev.specialization,
          education: response.user.education || prev.education,
          shortDescription: response.user.shortDescription || prev.shortDescription,
          therapyFor: response.user.therapyFor || prev.therapyFor,
          languages: response.user.languages || prev.languages,
          location: response.user.location || prev.location,
          yearsOfExperience: response.user.yearsOfExperience !== undefined ? response.user.yearsOfExperience : prev.yearsOfExperience,
          bio: response.user.bio || prev.bio,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
        if (authSession) {
          saveAuthSession({
            ...authSession,
            user: {
              ...authSession.user,
              ...response.user,
            }
          });
        }
      } else {
        setProfileData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
      }
      if (response.profile) {
        setProfile(response.profile);
      }

      setStatusMessage('Profile and account details saved successfully.');
    } catch (error) {
      setErrorMessage(error.message || 'Unable to save profile.');
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setDocumentData({ ...documentData, originalName: file.name });
    }
  };

  const handleDocumentSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (!documentData.fileUrl && !selectedFile) {
      setErrorMessage('Please choose a document file or enter a document URL.');
      return;
    }

    try {
      let finalUrl = documentData.fileUrl;
      let name = documentData.originalName || 'Document';

      if (selectedFile) {
        finalUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = (e) => reject(e);
          reader.readAsDataURL(selectedFile);
        });
        name = selectedFile.name;
      }

      const response = await physioApi.addDocument({
        documentType: documentData.documentType,
        fileUrl: finalUrl,
        originalName: name,
      });

      setDocuments((current) => [response.document, ...current]);
      setDocumentData({ documentType: 'license', fileUrl: '', originalName: '' });
      setSelectedFile(null);
      setStatusMessage('✅ Verification document uploaded successfully for review!');
    } catch (error) {
      setErrorMessage(error.message || 'Unable to add document for verification.');
    }
  };

  const updateBooking = async (bookingId, status) => {
    try {
      await bookingApi.updateStatus(bookingId, { status });
      const refreshed = await bookingApi.mine();
      setBookings(refreshed.bookings || []);
      setStatusMessage('Booking status updated.');
    } catch (error) {
      setErrorMessage(error.message || 'Unable to update booking.');
    }
  };

  // End / Close a therapy session with client
  const handleEndSession = async (booking) => {
    const clientName = booking.clientId?.name || booking.clientName || 'Patient';
    const clientPhone = booking.clientId?.phone || booking.clientPhone || '';
    const clientEmail = booking.clientId?.email || 'N/A';
    const clientId = booking.clientId?._id || booking.clientId;
    const scheduledDateStr = booking.scheduledAt ? new Date(booking.scheduledAt).toLocaleDateString() : 'N/A';

    if (!window.confirm(`Are you sure you want to end and mark closed the therapy session with ${clientName}?`)) {
      return;
    }

    try {
      await updateBooking(booking._id, 'completed');

      // Set WhatsApp target as completed session & open WhatsApp modal in read-only mode
      setWhatsappTarget({
        _id: clientId,
        name: clientName,
        phone: clientPhone,
        email: clientEmail,
        role: 'client',
        status: 'completed',
        isSessionCompleted: true
      });
      setInitialWhatsappMessage('');
      setWhatsappModalOpen(true);
      setStatusMessage(`Session with ${clientName} closed. Inbuilt WhatsApp messaging for this session is now disabled.`);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to end session');
    }
  };

  // Download chat transcript for a client
  const handleDownloadChatTranscript = async (clientId, clientName) => {
    try {
      const chatsRes = await chatApi.getChats();
      const rawChats = chatsRes.chats || [];
      const chat = rawChats.find(c =>
        c.participants && c.participants.some(p => {
          const pid = typeof p === 'object' ? String(p._id || p.id || '') : String(p);
          return pid === String(clientId);
        })
      );

      if (!chat) {
        alert(`No chat history found with ${clientName}.`);
        return;
      }

      const msgRes = await chatApi.getMessages(chat._id);
      const messages = msgRes.messages || [];

      if (messages.length === 0) {
        alert(`No chat messages found with ${clientName}.`);
        return;
      }

      let textContent = `====================================================\n`;
      textContent += `PSYCHMITRA THERAPY SESSION CHAT TRANSCRIPT\n`;
      textContent += `Client: ${clientName}\n`;
      textContent += `Export Date: ${new Date().toLocaleString()}\n`;
      textContent += `====================================================\n\n`;

      const currentUserId = authSession?.user?._id || authSession?.user?.id;
      messages.forEach(msg => {
        const isMe = String(msg.sender?._id || msg.sender) === String(currentUserId);
        const senderName = isMe ? 'Therapist' : clientName;
        const timeStr = msg.sentAt ? new Date(msg.sentAt).toLocaleString() : '';
        textContent += `[${timeStr}] ${senderName}: ${msg.content || ''}\n`;
      });

      const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Chat_Transcript_${clientName.replace(/\s+/g, '_')}_${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download chat error:', err);
      alert('Failed to download chat transcript: ' + (err.message || 'Error'));
    }
  };

  // Send chat transcript directly to client in internal messaging
  const handleSendChatToClient = async (clientId, clientName) => {
    try {
      const chatsRes = await chatApi.getChats();
      const rawChats = chatsRes.chats || [];
      const chat = rawChats.find(c =>
        c.participants && c.participants.some(p => {
          const pid = typeof p === 'object' ? String(p._id || p.id || '') : String(p);
          return pid === String(clientId);
        })
      );

      if (!chat) {
        alert(`No chat history found with ${clientName}.`);
        return;
      }

      const msgRes = await chatApi.getMessages(chat._id);
      const messages = msgRes.messages || [];

      if (messages.length === 0) {
        alert(`No chat messages found with ${clientName}.`);
        return;
      }

      let transcriptText = `📄 **Session Chat Transcript**\n\n`;
      const currentUserId = authSession?.user?._id || authSession?.user?.id;
      messages.forEach(msg => {
        const isMe = String(msg.sender?._id || msg.sender) === String(currentUserId);
        const senderName = isMe ? 'Therapist' : clientName;
        const timeStr = msg.sentAt ? new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
        transcriptText += `[${timeStr}] ${senderName}: ${msg.content || ''}\n`;
      });

      await chatApi.sendMessage(clientId, transcriptText);
      setStatusMessage(`Chat transcript successfully sent to ${clientName}!`);
      alert(`Chat transcript successfully sent to ${clientName}!`);
    } catch (err) {
      console.error('Send chat error:', err);
      alert('Failed to send chat transcript: ' + (err.message || 'Error'));
    }
  };

  // Handle blog cover image upload
  const handleBlogImageUpload = async (e, isEdit = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      if (isEdit) {
        setUploadingEditBlogImage(true);
      } else {
        setUploadingBlogImage(true);
      }
      setErrorMessage('');
      const data = new FormData();
      data.append('file', file);
      const res = await physioApi.uploadBlogImage(data);
      if (res?.url) {
        if (isEdit) {
          setEditBlogForm((prev) => ({ ...prev, featuredImage: res.url }));
        } else {
          setBlogForm((prev) => ({ ...prev, featuredImage: res.url }));
        }
        setStatusMessage('Cover image uploaded successfully!');
        setTimeout(() => setStatusMessage(''), 3000);
      } else {
        throw new Error(res?.message || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Image upload failed:', err);
      setErrorMessage(err.message || 'Image upload failed. You can paste an image URL directly.');
    } finally {
      if (isEdit) {
        setUploadingEditBlogImage(false);
      } else {
        setUploadingBlogImage(false);
      }
      e.target.value = '';
    }
  };

  // ==================== BLOG HANDLERS (MATCHING ADMIN SECTION) ====================
  const handleCreateBlog = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (!blogForm.title || blogForm.title.trim().length < 3) {
      setErrorMessage('Title must be at least 3 characters long');
      return;
    }

    const fullContent = (blogForm.content && blogForm.content.trim().length >= 10)
      ? blogForm.content.trim()
      : (blogForm.excerpt && blogForm.excerpt.trim().length >= 10)
        ? blogForm.excerpt.trim()
        : (blogForm.title.trim() + ' - Article description and full content details.');

    const payload = {
      title: blogForm.title.trim(),
      category: blogForm.category || 'Mental Health',
      excerpt: blogForm.excerpt?.trim() || fullContent.substring(0, 150),
      content: fullContent,
      featuredImage: blogForm.featuredImage?.trim() || '',
    };

    try {
      const response = await physioApi.addBlog(payload);
      const created = response.blog || response;
      setBlogs([created, ...blogs]);
      setBlogForm({
        title: '',
        author: authSession?.user?.name ? `Dr. ${authSession.user.name}` : '',
        category: blogCategories[0] || 'Mental Health',
        excerpt: '',
        content: '',
        featuredImage: ''
      });
      setStatusMessage('Blog article submitted successfully! Awaiting admin approval.');
      setTimeout(() => setStatusMessage(''), 4000);
    } catch (error) {
      console.error('Error creating blog:', error);
      setErrorMessage(error.message || 'Failed to create blog article');
    }
  };

  const handleUpdateBlog = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (!editBlogId) return;

    if (!editBlogForm.title || editBlogForm.title.trim().length < 3) {
      setErrorMessage('Title must be at least 3 characters long');
      return;
    }

    const fullContent = (editBlogForm.content && editBlogForm.content.trim().length >= 10)
      ? editBlogForm.content.trim()
      : (editBlogForm.excerpt && editBlogForm.excerpt.trim().length >= 10)
        ? editBlogForm.excerpt.trim()
        : (editBlogForm.title.trim() + ' - Article description and full content details.');

    const payload = {
      title: editBlogForm.title.trim(),
      category: editBlogForm.category || 'Mental Health',
      excerpt: editBlogForm.excerpt?.trim() || fullContent.substring(0, 150),
      content: fullContent,
      featuredImage: editBlogForm.featuredImage?.trim() || '',
    };

    try {
      const response = await physioApi.updateBlog(editBlogId, payload);
      const updated = response.blog || response;
      setBlogs(blogs.map(b => ((b._id === editBlogId || b.id === editBlogId) ? { ...b, ...updated, ...payload } : b)));
      setEditBlogId(null);
      setEditBlogForm({
        title: '',
        author: '',
        category: blogCategories[0] || 'Mental Health',
        excerpt: '',
        content: '',
        featuredImage: ''
      });
      setStatusMessage('Blog article updated successfully!');
      setTimeout(() => setStatusMessage(''), 4000);
    } catch (error) {
      console.error('Error updating blog:', error);
      setErrorMessage(error.message || 'Failed to update blog article');
    }
  };

  const handleDeleteBlog = async (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        await physioApi.deleteBlog(blogId);
        setBlogs(blogs.filter(b => b._id !== blogId));
        setStatusMessage('Blog deleted successfully!');
      } catch (error) {
        setErrorMessage(error.message || 'Failed to delete blog');
      }
    }
  };

  // ==================== EVENT HANDLERS ====================
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (!eventForm.title || !eventForm.scheduledAt) {
      setErrorMessage('Event title and scheduled date/time are required.');
      return;
    }

    const isFirstFree = Boolean(eventEligibility.isFirstFreeEligible);
    const chosenPlan = (eventEligibility.plans || []).find(p => p._id === selectedPlanId) || eventEligibility.plans?.[0];

    if (!isFirstFree && !chosenPlan) {
      setErrorMessage('Please select an Event Publish Plan to host your event.');
      return;
    }

    try {
      setIsProcessingEvent(true);

      // Case A: 1st Event Free for Pro Member
      if (isFirstFree) {
        const response = await eventsApi.addEvent({
          title: eventForm.title,
          description: eventForm.description,
          eventType: eventForm.eventType,
          mode: eventForm.mode,
          scheduledAt: eventForm.scheduledAt,
          endsAt: eventForm.endsAt,
          location: eventForm.location,
          eventFee: eventForm.eventFee || 'Free',
          registrationLink: eventForm.registrationLink || '',
          videoUrl: eventForm.videoUrl || '',
          videoFileName: eventForm.videoFileName || '',
          videoFileSize: parseInt(eventForm.videoFileSize, 10) || 0,
        });

        setStatusMessage(response.message || '🎉 Event published for FREE for 1 week as a Pro Member perk!');
        setEventForm({ title: '', description: '', eventType: 'seminar', mode: 'online', scheduledAt: '', endsAt: '', location: '', eventFee: 'Free', registrationLink: '', videoUrl: '', videoFileName: '', videoFileSize: 0 });
        setShowEventForm(false);
        await fetchEventsAndEligibility();
        return;
      }

      // Case B: Paid Event Publish Plan
      const orderRes = await eventsApi.createPublishOrder({
        planId: chosenPlan._id,
        action: 'create',
      });

      const loaded = await loadRazorpaySDK();
      if (!loaded || !window.Razorpay) {
        // Fallback simulated payment for local/test environments
        const response = await eventsApi.addEvent({
          title: eventForm.title,
          description: eventForm.description,
          eventType: eventForm.eventType,
          mode: eventForm.mode,
          scheduledAt: eventForm.scheduledAt,
          endsAt: eventForm.endsAt,
          location: eventForm.location,
          eventFee: eventForm.eventFee || 'Free',
          registrationLink: eventForm.registrationLink || '',
          videoUrl: eventForm.videoUrl || '',
          videoFileName: eventForm.videoFileName || '',
          videoFileSize: parseInt(eventForm.videoFileSize, 10) || 0,
          publishPlanId: chosenPlan._id,
          paymentId: `pay_sim_${Date.now()}`,
          orderId: orderRes.orderId || `ord_${Date.now()}`,
        });
        setStatusMessage(`✓ Event published successfully under ${chosenPlan.name}!`);
        setEventForm({ title: '', description: '', eventType: 'seminar', mode: 'online', scheduledAt: '', endsAt: '', location: '', eventFee: 'Free', registrationLink: '', videoUrl: '', videoFileName: '', videoFileSize: 0 });
        setShowEventForm(false);
        await fetchEventsAndEligibility();
        return;
      }

      // Open Razorpay Checkout Modal
      const rzp = new window.Razorpay({
        key: orderRes.keyId || process.env.REACT_APP_RAZORPAY_KEY || 'rzp_test_mockkey123',
        amount: orderRes.amount * 100,
        currency: orderRes.currency || 'INR',
        name: 'PsychMitra Events',
        description: `Publish Event: ${chosenPlan.name}`,
        order_id: orderRes.orderId && !orderRes.orderId.startsWith('rzp_evt_') ? orderRes.orderId : undefined,
        prefill: {
          name: authSession?.user?.name || '',
          email: authSession?.user?.email || '',
        },
        theme: { color: '#7c3aed' },
        handler: async (paymentResponse) => {
          try {
            const response = await eventsApi.addEvent({
              title: eventForm.title,
              description: eventForm.description,
              eventType: eventForm.eventType,
              mode: eventForm.mode,
              scheduledAt: eventForm.scheduledAt,
              endsAt: eventForm.endsAt,
              location: eventForm.location,
              eventFee: eventForm.eventFee || 'Free',
              registrationLink: eventForm.registrationLink || '',
              videoUrl: eventForm.videoUrl || '',
              videoFileName: eventForm.videoFileName || '',
              videoFileSize: parseInt(eventForm.videoFileSize, 10) || 0,
              publishPlanId: chosenPlan._id,
              paymentId: paymentResponse.razorpay_payment_id || `pay_${Date.now()}`,
              orderId: paymentResponse.razorpay_order_id || orderRes.orderId,
            });
            setStatusMessage(`✓ Event published successfully under ${chosenPlan.name}!`);
            setEventForm({ title: '', description: '', eventType: 'seminar', mode: 'online', scheduledAt: '', endsAt: '', location: '', eventFee: 'Free', registrationLink: '', videoUrl: '', videoFileName: '', videoFileSize: 0 });
            setShowEventForm(false);
            await fetchEventsAndEligibility();
          } catch (err) {
            setErrorMessage(err.message || 'Failed to complete event creation.');
          }
        },
      });

      rzp.open();

    } catch (error) {
      console.error('Create event error:', error);
      setErrorMessage(error.message || 'Failed to create event');
    } finally {
      setIsProcessingEvent(false);
    }
  };

  const handleOpenExtendModal = (event) => {
    const defaultPlan = eventEligibility.plans?.[0]?._id || '';
    setExtendModal({
      isOpen: true,
      event,
      planId: defaultPlan,
      isProcessing: false,
    });
  };

  const handleConfirmExtend = async () => {
    if (!extendModal.event || !extendModal.planId) {
      setErrorMessage('Please select an extension plan.');
      return;
    }

    const chosenPlan = (eventEligibility.plans || []).find(p => p._id === extendModal.planId);
    if (!chosenPlan) return;

    try {
      setExtendModal(prev => ({ ...prev, isProcessing: true }));
      setErrorMessage('');

      const orderRes = await eventsApi.createPublishOrder({
        planId: chosenPlan._id,
        eventId: extendModal.event._id,
        action: 'extend',
      });

      const loaded = await loadRazorpaySDK();
      if (!loaded || !window.Razorpay) {
        // Direct simulation fallback
        const res = await eventsApi.extendEvent(extendModal.event._id, {
          planId: chosenPlan._id,
          paymentId: `pay_ext_${Date.now()}`,
          orderId: orderRes.orderId,
        });
        setStatusMessage(res.message || '✓ Event extended successfully!');
        setExtendModal({ isOpen: false, event: null, planId: '', isProcessing: false });
        await fetchEventsAndEligibility();
        return;
      }

      const rzp = new window.Razorpay({
        key: orderRes.keyId || process.env.REACT_APP_RAZORPAY_KEY || 'rzp_test_mockkey123',
        amount: orderRes.amount * 100,
        currency: orderRes.currency || 'INR',
        name: 'PsychMitra Events',
        description: `Extend Listing: ${chosenPlan.name}`,
        order_id: orderRes.orderId && !orderRes.orderId.startsWith('rzp_evt_') ? orderRes.orderId : undefined,
        prefill: {
          name: authSession?.user?.name || '',
          email: authSession?.user?.email || '',
        },
        theme: { color: '#7c3aed' },
        handler: async (paymentResponse) => {
          try {
            const res = await eventsApi.extendEvent(extendModal.event._id, {
              planId: chosenPlan._id,
              paymentId: paymentResponse.razorpay_payment_id || `pay_${Date.now()}`,
              orderId: paymentResponse.razorpay_order_id || orderRes.orderId,
            });
            setStatusMessage(res.message || '✓ Event extended successfully!');
            setExtendModal({ isOpen: false, event: null, planId: '', isProcessing: false });
            await fetchEventsAndEligibility();
          } catch (err) {
            setErrorMessage(err.message || 'Failed to extend event.');
          }
        },
      });

      rzp.open();
    } catch (err) {
      console.error('Extend event error:', err);
      setErrorMessage(err.message || 'Failed to extend event.');
    } finally {
      setExtendModal(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const handleOpenRepublishModal = (event) => {
    const defaultPlan = eventEligibility.plans?.[0]?._id || '';
    setRepublishModal({
      isOpen: true,
      event,
      planId: defaultPlan,
      scheduledAt: event.scheduledAt ? new Date(event.scheduledAt).toISOString().slice(0, 16) : '',
      endsAt: event.endsAt ? new Date(event.endsAt).toISOString().slice(0, 16) : '',
      isProcessing: false,
    });
  };

  const handleConfirmRepublish = async () => {
    if (!republishModal.event || !republishModal.planId) {
      setErrorMessage('Please select a publish plan.');
      return;
    }

    const chosenPlan = (eventEligibility.plans || []).find(p => p._id === republishModal.planId);
    if (!chosenPlan) return;

    try {
      setRepublishModal(prev => ({ ...prev, isProcessing: true }));
      setErrorMessage('');

      const orderRes = await eventsApi.createPublishOrder({
        planId: chosenPlan._id,
        eventId: republishModal.event._id,
        action: 'republish',
      });

      const loaded = await loadRazorpaySDK();
      if (!loaded || !window.Razorpay) {
        // Direct simulation fallback
        const res = await eventsApi.republishEvent(republishModal.event._id, {
          planId: chosenPlan._id,
          scheduledAt: republishModal.scheduledAt,
          endsAt: republishModal.endsAt,
          paymentId: `pay_rep_${Date.now()}`,
          orderId: orderRes.orderId,
        });
        setStatusMessage(res.message || '🎉 Event republished successfully!');
        setRepublishModal({ isOpen: false, event: null, planId: '', scheduledAt: '', endsAt: '', isProcessing: false });
        await fetchEventsAndEligibility();
        return;
      }

      const rzp = new window.Razorpay({
        key: orderRes.keyId || process.env.REACT_APP_RAZORPAY_KEY || 'rzp_test_mockkey123',
        amount: orderRes.amount * 100,
        currency: orderRes.currency || 'INR',
        name: 'PsychMitra Events',
        description: `Republish Event: ${chosenPlan.name}`,
        order_id: orderRes.orderId && !orderRes.orderId.startsWith('rzp_evt_') ? orderRes.orderId : undefined,
        prefill: {
          name: authSession?.user?.name || '',
          email: authSession?.user?.email || '',
        },
        theme: { color: '#7c3aed' },
        handler: async (paymentResponse) => {
          try {
            const res = await eventsApi.republishEvent(republishModal.event._id, {
              planId: chosenPlan._id,
              scheduledAt: republishModal.scheduledAt,
              endsAt: republishModal.endsAt,
              paymentId: paymentResponse.razorpay_payment_id || `pay_${Date.now()}`,
              orderId: paymentResponse.razorpay_order_id || orderRes.orderId,
            });
            setStatusMessage(res.message || '🎉 Event republished successfully!');
            setRepublishModal({ isOpen: false, event: null, planId: '', scheduledAt: '', endsAt: '', isProcessing: false });
            await fetchEventsAndEligibility();
          } catch (err) {
            setErrorMessage(err.message || 'Failed to republish event.');
          }
        },
      });

      rzp.open();
    } catch (err) {
      console.error('Republish event error:', err);
      setErrorMessage(err.message || 'Failed to republish event.');
    } finally {
      setRepublishModal(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const handleStartEditEvent = (evt) => {
    setEditEventId(evt._id);
    let schedStr = '';
    if (evt.scheduledAt) {
      try {
        const d = new Date(evt.scheduledAt);
        schedStr = isNaN(d.getTime()) ? '' : new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      } catch {
        schedStr = '';
      }
    }
    setEditEventForm({
      title: evt.title || '',
      description: evt.description || '',
      eventType: evt.eventType || 'seminar',
      mode: evt.mode || 'online',
      scheduledAt: schedStr,
      location: evt.location || '',
      eventFee: evt.eventFee || 'Free',
      registrationLink: evt.registrationLink || '',
      videoUrl: evt.videoUrl || '',
      videoFileName: evt.videoFileName || '',
      videoFileSize: evt.videoFileSize || 0,
    });
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (!editEventId) return;

    try {
      await eventsApi.updateEvent(editEventId, {
        title: editEventForm.title,
        description: editEventForm.description,
        eventType: editEventForm.eventType,
        mode: editEventForm.mode,
        scheduledAt: editEventForm.scheduledAt,
        location: editEventForm.location,
        eventFee: editEventForm.eventFee || 'Free',
        registrationLink: editEventForm.registrationLink || '',
        videoUrl: editEventForm.videoUrl || '',
        videoFileName: editEventForm.videoFileName || '',
        videoFileSize: parseInt(editEventForm.videoFileSize, 10) || 0,
      });
      setEditEventId(null);
      setEditEventForm({
        title: '',
        description: '',
        eventType: 'seminar',
        mode: 'online',
        scheduledAt: '',
        location: '',
        eventFee: 'Free',
        registrationLink: '',
        videoUrl: '',
        videoFileName: '',
        videoFileSize: 0,
      });
      setStatusMessage('✓ Event updated successfully! Plan duration and expiry date adjusted.');
      await fetchEventsAndEligibility();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to update event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await eventsApi.deleteEvent(eventId);
        setEvents(events.filter(e => e._id !== eventId));
        setStatusMessage('Event deleted successfully!');
      } catch (error) {
        setErrorMessage(error.message || 'Failed to delete event');
      }
    }
  };

  // ==================== NOTE HANDLERS ====================
  const buildWorksheetPayload = (formState) => {
    if (!formState.selectedWorksheetId) {
      return { hasWorksheet: false };
    }

    const selectedWs = availableWorksheets.find(w => w._id === formState.selectedWorksheetId);
    if (selectedWs) {
      return {
        hasWorksheet: true,
        worksheetId: selectedWs._id,
        title: selectedWs.title,
        type: 'admin_sheet',
        fileUrl: selectedWs.fileUrl || '',
        fileName: selectedWs.fileName || '',
        fileSize: selectedWs.fileSize || 0,
        sheetData: {
          title: selectedWs.title,
          columns: ['Date / Step', 'Prompt / Exercise Section', 'Client Response', 'Therapist Feedback / Notes'],
          rows: [['Step 1', selectedWs.title, '', '']],
        },
      };
    }

    return { hasWorksheet: false };
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (!noteForm.clientName || noteForm.clientName === '__custom__' || !noteForm.note) {
      setErrorMessage('Client name and note content are required');
      return;
    }

    const worksheetPayload = buildWorksheetPayload(noteForm);

    try {
      const payload = {
        clientName: noteForm.clientName,
        clientId: noteForm.clientId || null,
        clientEmail: noteForm.clientEmail || '',
        note: noteForm.note,
        worksheet: worksheetPayload,
      };

      const response = await clientNoteApi.addNote(payload);
      setNotes([response.note || response, ...notes]);
      setNoteForm(initialNoteFormState);
      setStatusMessage(
        worksheetPayload.hasWorksheet
          ? '✅ Session note created & live worksheet shared with client in My Worksheets!'
          : '✅ Private session note created successfully (only visible to therapist).'
      );
    } catch (error) {
      setErrorMessage(error.message || 'Failed to create note');
    }
  };

  const handleUpdateNote = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (!editNoteId || !editNoteForm.clientName || !editNoteForm.note) {
      setErrorMessage('Client name and note content are required');
      return;
    }

    const worksheetPayload = buildWorksheetPayload(editNoteForm);

    try {
      const payload = {
        clientName: editNoteForm.clientName,
        clientId: editNoteForm.clientId || null,
        clientEmail: editNoteForm.clientEmail || '',
        note: editNoteForm.note,
        worksheet: worksheetPayload,
      };

      const response = await clientNoteApi.updateNote(editNoteId, payload);
      const updated = response.note || response;
      setNotes(notes.map(n => (n._id === editNoteId || n.id === editNoteId) ? updated : n));
      setEditNoteId(null);
      setEditNoteForm(initialNoteFormState);
      setStatusMessage('Session note updated successfully!');
    } catch (error) {
      setErrorMessage(error.message || 'Failed to update session note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (window.confirm('Are you sure you want to delete this session note?')) {
      try {
        await clientNoteApi.deleteNote(noteId);
        setNotes(notes.filter(n => n._id !== noteId && n.id !== noteId));
        setStatusMessage('Session note deleted successfully!');
      } catch (error) {
        setErrorMessage(error.message || 'Failed to delete note');
      }
    }
  };

  // Download single session note as formatted text file
  const handleDownloadSingleNote = (note) => {
    if (!note) return;
    const therapistName = user?.name || authSession?.user?.name || 'Therapist';
    const clientName = note.clientName || 'Client';
    const dateStr = note.createdAt ? new Date(note.createdAt).toLocaleString() : new Date().toLocaleString();

    const fileContent = `===================================================================
                  PSYCHMITRA CLINICAL SESSION NOTE
===================================================================

Client Name:   ${clientName}
Practitioner:  ${therapistName}
Date Record:   ${dateStr}
Note ID:       ${note._id || note.id || 'N/A'}
-------------------------------------------------------------------
CLINICAL OBSERVATIONS & NOTES:
-------------------------------------------------------------------
${note.note || 'No notes recorded.'}

-------------------------------------------------------------------
Confidential Medical & Psychological Record - PsychMitra Platform
===================================================================`;

    const blob = new Blob([fileContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const sanitizedClient = clientName.replace(/[^a-zA-Z0-9]/g, '_');
    link.download = `Session_Note_${sanitizedClient}_${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download all session notes report
  const handleDownloadAllNotes = () => {
    if (!notes || notes.length === 0) {
      alert('No session notes available to download.');
      return;
    }

    const therapistName = user?.name || authSession?.user?.name || 'Therapist';
    let reportContent = `===================================================================
                PSYCHMITRA CLIENT SESSION NOTES SUMMARY REPORT
===================================================================
Practitioner:     ${therapistName}
Total Notes:      ${notes.length}
Export Date:      ${new Date().toLocaleString()}
===================================================================\n\n`;

    notes.forEach((note, index) => {
      const dateStr = note.createdAt ? new Date(note.createdAt).toLocaleString() : 'N/A';
      reportContent += `-------------------------------------------------------------------
NOTE #${index + 1} | Client: ${note.clientName || 'Unknown'} | Date: ${dateStr}
-------------------------------------------------------------------
${note.note || 'No notes recorded.'}\n\n`;
    });

    reportContent += `===================================================================
Confidential Medical & Psychological Record - PsychMitra Platform
===================================================================`;

    const blob = new Blob([reportContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Client_Notes_Summary_Report_${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'analytics':
        return (
          <TherapistAnalyticsView
            bookings={bookings}
            profile={profile}
            user={user}
            events={events}
            blogs={blogs}
            notes={notes}
          />
        );
      case 'dashboard':
        return (
          <div className="physio-dashboard__overview fade-in">
            {/* Quick Navigation Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div
                onClick={() => setActiveTab('analytics')}
                style={{ background: '#fff', padding: '18px 20px', borderRadius: '12px', border: '1.5px solid #ddd6fe', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(124, 58, 237, 0.08)' }}
              >
                <div style={{ fontSize: '28px', background: '#f5f3ff', width: '50px', height: '50px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📊</div>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#7c3aed', fontWeight: 700 }}>Practice Analytics</h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Revenue, charts & graphs →</p>
                </div>
              </div>

              <div
                onClick={() => setActiveTab('bookings')}
                style={{ background: '#fff', padding: '18px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}
              >
                <div style={{ fontSize: '28px', background: '#eff6ff', width: '50px', height: '50px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📅</div>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#0f172a' }}>Manage Bookings</h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{bookings.length} Total Sessions</p>
                </div>
              </div>

              <div
                onClick={() => setActiveTab('profile')}
                style={{ background: '#fff', padding: '18px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}
              >
                <div style={{ fontSize: '28px', background: '#f5f3ff', width: '50px', height: '50px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>👨‍⚕️</div>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#0f172a' }}>Profile & Slots</h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Edit fee & time slots</p>
                </div>
              </div>

              <div
                onClick={() => setActiveTab('worksheets')}
                style={{ background: '#fff', padding: '18px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}
              >
                <div style={{ fontSize: '28px', background: '#ecfdf5', width: '50px', height: '50px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📑</div>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#0f172a' }}>Clinical Worksheets</h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Access therapy tools</p>
                </div>
              </div>

              <div
                onClick={() => setActiveTab('events')}
                style={{ background: '#fff', padding: '18px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}
              >
                <div style={{ fontSize: '28px', background: '#fff7ed', width: '50px', height: '50px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🎉</div>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#0f172a' }}>Events & Webinars</h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Publish seminars</p>
                </div>
              </div>
            </div>

            {/* Quick Analytics Mini Snapshot Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #1e1335 0%, #31185a 100%)',
              borderRadius: '16px',
              padding: '20px 24px',
              color: '#ffffff',
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
                  📊
                </div>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 800, color: '#ffffff' }}>
                    Practice Analytics & Performance Graphs
                  </h4>
                  <p style={{ margin: 0, fontSize: '13px', color: '#cbd5e1' }}>
                    View monthly revenue curves, peak consultation days, and client satisfaction charts.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setActiveTab('analytics')}
                style={{
                  background: '#ffffff',
                  color: '#7c3aed',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  fontWeight: 800,
                  fontSize: '13.5px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              >
                View Full Analytics →
              </button>
            </div>

            {/* Recent Bookings Section */}
            <div className="physio-dashboard__card" style={{ marginBottom: '24px' }}>
              <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0 }}>📅 Recent Appointments & Sessions</h3>
                <button onClick={() => setActiveTab('bookings')} className="btn-secondary" style={{ fontSize: '13px', padding: '6px 14px' }}>View All Bookings →</button>
              </div>

              {bookings && bookings.length > 0 ? (
                <div className="table-responsive">
                  <table className="physio-table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Date & Time</th>
                        <th>Type</th>
                        <th>Fee</th>
                        <th>Status</th>
                        <th>Rating</th>
                        <th>Quick Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.slice(0, 5).map((b) => (
                        <tr key={b._id}>
                          <td>
                            <strong>{b.clientId?.name || b.clientName || 'Patient'}</strong>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>{b.clientId?.phone || b.clientPhone || ''}</div>
                          </td>
                          <td>
                            {b.scheduledAt ? new Date(b.scheduledAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : 'Flexible / TBD'}
                          </td>
                          <td>
                            <span style={{ textTransform: 'capitalize', fontSize: '12px', fontWeight: 600 }}>{b.sessionType || '1-on-1 Session'}</span>
                          </td>
                          <td>₹{b.amount || b.fee || 999}</td>
                          <td>
                            <span className={`status-pill ${b.status || 'pending'}`}>
                              {b.status || 'pending'}
                            </span>
                          </td>
                          <td>
                            {b.rating ? (
                              <span style={{ color: '#f59e0b', fontWeight: 600 }}>⭐ {b.rating}</span>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: '12px' }}>No review yet</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                              {b.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => updateBooking(b._id, 'confirmed')}
                                    style={{ background: '#10b981', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                                    title="Accept Session"
                                  >
                                    ✓ Accept
                                  </button>
                                  <button
                                    onClick={() => {
                                      setBookingActionModalTarget(b);
                                      setBookingActionModalType('reschedule');
                                      setBookingActionModalOpen(true);
                                    }}
                                    style={{ background: '#7c3aed', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                                    title="Reschedule Session"
                                  >
                                    🔄 Reschedule
                                  </button>
                                  <button
                                    onClick={() => {
                                      setBookingActionModalTarget(b);
                                      setBookingActionModalType('cancel');
                                      setBookingActionModalOpen(true);
                                    }}
                                    style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                                    title="Decline / Cancel Session"
                                  >
                                    ✕ Decline
                                  </button>
                                </>
                              )}
                              {b.status === 'confirmed' && (
                                <span style={{ color: '#059669', fontSize: '12px', fontWeight: 600 }}>
                                  ✓ Confirmed
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>🗓️</div>
                  <p style={{ margin: 0, fontWeight: 500 }}>No bookings received yet.</p>
                  <p style={{ margin: '6px 0 0 0', fontSize: '13px' }}>Your upcoming appointments from clients will appear here.</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'profile':
        return (
          <div className="physio-dashboard__card fade-in">
            <div className="section-header">
              <h3>👤 Practitioner Profile & Account Settings</h3>
              <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                Update your professional credentials, contact details, and account security
              </p>
            </div>
            <form onSubmit={handleProfileSubmit} className="physio-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    required
                    value={profileData.name || ''}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    placeholder="e.g., Dr. Rajesh Sharma"
                  />
                </div>
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    required
                    value={profileData.email || ''}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    placeholder="doctor@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    required
                    value={profileData.phone || ''}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    placeholder="e.g., +91 98765 43210"
                  />
                </div>
                <div className="form-group">
                  <label>Specialization / Clinical Focus</label>
                  <input
                    type="text"
                    value={profileData.specialization || ''}
                    onChange={(e) => setProfileData({ ...profileData, specialization: e.target.value })}
                    placeholder="e.g., Clinical Psychology, CBT"
                  />
                </div>
                <div className="form-group">
                  <label>🎓 Education & Qualifications</label>
                  <input
                    type="text"
                    value={profileData.education || ''}
                    onChange={(e) => setProfileData({ ...profileData, education: e.target.value })}
                    placeholder="e.g., M.A. Clinical Psychology, M.Phil, RCI Licensed"
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                    {quickEducationSuggestionsDash.map((deg) => (
                      <button
                        key={deg}
                        type="button"
                        onClick={() => {
                          if (!profileData.education) {
                            setProfileData({ ...profileData, education: deg });
                          } else if (!profileData.education.includes(deg)) {
                            setProfileData({ ...profileData, education: profileData.education + ', ' + deg });
                          }
                        }}
                        style={{
                          background: '#f1f5f9',
                          border: '1px solid #cbd5e1',
                          borderRadius: '12px',
                          padding: '2px 8px',
                          fontSize: '11px',
                          color: '#475569',
                          cursor: 'pointer',
                        }}
                      >
                        + {deg}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Location (City, Clinic)</label>
                  <input
                    type="text"
                    value={profileData.location || ''}
                    onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                    placeholder="e.g., Mumbai, Maharashtra"
                  />
                </div>
                <div className="form-group">
                  <label>⏳ Years of Experience</label>
                  <input
                    type="number"
                    min="0"
                    value={profileData.yearsOfExperience || 0}
                    onChange={(e) => setProfileData({ ...profileData, yearsOfExperience: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Membership Tier</label>
                  <input
                    type="text"
                    value={profileData.membership === 'pro' ? '⭐ Pro Practitioner' : 'Free Practitioner'}
                    readOnly
                    style={{ background: '#f1f5f9', cursor: 'not-allowed', color: '#475569', fontWeight: 600 }}
                  />
                </div>
              </div>

              {/* SHORT DESCRIPTION / HEADLINE */}
              <div className="form-group" style={{ marginTop: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <label style={{ margin: 0, fontWeight: 600 }}>📝 Short Description / Professional Headline</label>
                  <span style={{ fontSize: '11px', color: (profileData.shortDescription || '').length > 180 ? '#ef4444' : '#64748b' }}>
                    {(profileData.shortDescription || '').length}/180 chars
                  </span>
                </div>
                <input
                  type="text"
                  value={profileData.shortDescription || ''}
                  onChange={(e) => setProfileData({ ...profileData, shortDescription: e.target.value.slice(0, 220) })}
                  placeholder="e.g. Senior Clinical Psychologist specializing in Cognitive Behavioral Therapy & anxiety relief"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
                <small style={{ color: '#64748b', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  A concise 1-2 sentence tagline displayed on your public profile card and client search results.
                </small>
              </div>

              {/* I OFFER THERAPY FOR */}
              <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '10px', padding: '16px', marginTop: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ margin: 0, fontWeight: 700, color: '#581c87', fontSize: '14px' }}>
                    🧠 I Offer Therapy For ({(profileData.therapyFor || []).length} Selected)
                  </label>
                </div>
                <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#64748b' }}>
                  Select all mental health issues and concerns you treat:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                  {defaultTherapyOptionsDash.map((item) => {
                    const isSelected = (profileData.therapyFor || []).includes(item);
                    return (
                      <button
                        key={item}
                        type="button"
                        onClick={() => toggleTherapyDash(item)}
                        style={{
                          padding: '5px 12px',
                          borderRadius: '16px',
                          fontSize: '12px',
                          fontWeight: isSelected ? 600 : 500,
                          border: isSelected ? '1px solid #7c3aed' : '1px solid #cbd5e1',
                          background: isSelected ? '#7c3aed' : '#fff',
                          color: isSelected ? '#fff' : '#475569',
                          cursor: 'pointer',
                        }}
                      >
                        {isSelected ? '✓ ' : '+ '} {item}
                      </button>
                    );
                  })}
                  {(profileData.therapyFor || []).filter((i) => !defaultTherapyOptionsDash.includes(i)).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => toggleTherapyDash(item)}
                      style={{
                        padding: '5px 12px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: 600,
                        border: '1px solid #059669',
                        background: '#059669',
                        color: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      ✓ {item} ✕
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="Add custom concern / specialty (e.g. Phobias, Postpartum)..."
                    value={customTherapyDash}
                    onChange={(e) => setCustomTherapyDash(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomTherapyDash();
                      }
                    }}
                    style={{ flex: 1, padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                  <button
                    type="button"
                    onClick={addCustomTherapyDash}
                    style={{ padding: '8px 16px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    + Add
                  </button>
                </div>
              </div>

              {/* LANGUAGES */}
              <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px', padding: '16px', marginTop: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ margin: 0, fontWeight: 700, color: '#0369a1', fontSize: '14px' }}>
                    🗣️ Languages Spoken ({(profileData.languages || []).length} Selected)
                  </label>
                </div>
                <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#64748b' }}>
                  Select languages in which you can conduct therapy sessions:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                  {defaultLanguageOptionsDash.map((lang) => {
                    const isSelected = (profileData.languages || []).includes(lang);
                    return (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => toggleLanguageDash(lang)}
                        style={{
                          padding: '5px 12px',
                          borderRadius: '16px',
                          fontSize: '12px',
                          fontWeight: isSelected ? 600 : 500,
                          border: isSelected ? '1px solid #0284c7' : '1px solid #cbd5e1',
                          background: isSelected ? '#0284c7' : '#fff',
                          color: isSelected ? '#fff' : '#475569',
                          cursor: 'pointer',
                        }}
                      >
                        {isSelected ? '✓ ' : '+ '} {lang}
                      </button>
                    );
                  })}
                  {(profileData.languages || []).filter((l) => !defaultLanguageOptionsDash.includes(l)).map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => toggleLanguageDash(lang)}
                      style={{
                        padding: '5px 12px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: 600,
                        border: '1px solid #059669',
                        background: '#059669',
                        color: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      ✓ {lang} ✕
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="Add another language..."
                    value={customLanguageDash}
                    onChange={(e) => setCustomLanguageDash(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomLanguageDash();
                      }
                    }}
                    style={{ flex: 1, padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                  <button
                    type="button"
                    onClick={addCustomLanguageDash}
                    style={{ padding: '8px 16px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    + Add
                  </button>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '15px' }}>
                <label>Professional Bio / Summary</label>
                <textarea
                  value={profileData.bio || ''}
                  onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                  rows="4"
                  placeholder="Share your expertise, qualifications, and therapeutic approaches..."
                ></textarea>
              </div>

              {/* Password & Security Section */}
              <div style={{
                marginTop: '30px',
                paddingTop: '20px',
                borderTop: '1px solid #e2e8f0',
                marginBottom: '20px'
              }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🔒</span> Update Password (Optional)
                </h4>
                <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '13px' }}>
                  Leave these fields blank if you do not wish to change your password.
                </p>

                <div className="form-grid">
                  <div className="form-group">
                    <label>Current Password</label>
                    <input
                      type="password"
                      value={profileData.currentPassword || ''}
                      onChange={(e) => setProfileData({ ...profileData, currentPassword: e.target.value })}
                      placeholder="Enter current password"
                    />
                  </div>
                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      value={profileData.newPassword || ''}
                      onChange={(e) => setProfileData({ ...profileData, newPassword: e.target.value })}
                      placeholder="Min. 6 characters"
                    />
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      value={profileData.confirmPassword || ''}
                      onChange={(e) => setProfileData({ ...profileData, confirmPassword: e.target.value })}
                      placeholder="Repeat new password"
                    />
                  </div>
                </div>
              </div>

              <button type="submit" className="btn-primary" style={{ padding: '12px 28px', fontSize: '15px', fontWeight: 600 }}>
                💾 Save Profile Changes
              </button>
            </form>

            <TherapistSlotManager />
          </div>
        );
      case 'documents':
        return (
          <div className="physio-dashboard__card fade-in">
            <div className="section-header" style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: '0 0 5px 0' }}>📜 Practitioner Verification Credentials</h3>
                  <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                    Upload medical licenses, degrees, and ID proof to complete account verification.
                  </p>
                </div>
                <div style={{
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: 600,
                  backgroundColor: profile?.verificationStatus === 'approved' ? '#d1fae5' : profile?.verificationStatus === 'rejected' ? '#fee2e2' : '#fef3c7',
                  color: profile?.verificationStatus === 'approved' ? '#065f46' : profile?.verificationStatus === 'rejected' ? '#991b1b' : '#92400e',
                }}>
                  {profile?.verificationStatus === 'approved' ? '✓ Verified Account' : profile?.verificationStatus === 'rejected' ? '✗ Verification Rejected' : '⏳ Verification Pending'}
                </div>
              </div>
            </div>

            <div style={{
              background: '#f8f9fa',
              padding: '15px 20px',
              borderRadius: '8px',
              marginBottom: '25px',
              borderLeft: '4px solid #7c3aed',
              fontSize: '14px'
            }}>
              <strong>Logged-in Practitioner:</strong> {user?.name || 'Practitioner'} ({user?.email || 'N/A'})
            </div>

            <form onSubmit={handleDocumentSubmit} className="physio-form" style={{ marginBottom: '30px' }}>
              <div className="form-grid">
                <div className="form-group">
                  <label style={{ fontWeight: 600 }}>Document Type *</label>
                  <select
                    value={documentData.documentType}
                    onChange={(e) => setDocumentData({ ...documentData, documentType: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                  >
                    <option value="license">Medical License / Registration</option>
                    <option value="degree">Degree Certificate (PhD / MSc / MD)</option>
                    <option value="identity">Government ID Proof (Aadhaar / Passport / PAN)</option>
                    <option value="certificate">Specialization Certificate</option>
                    <option value="other">Other Credential</option>
                  </select>
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: 600 }}>Choose File *</label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff' }}
                  />
                  {selectedFile && (
                    <small style={{ color: '#059669', display: 'block', marginTop: '4px', fontWeight: 500 }}>
                      Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                    </small>
                  )}
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '10px' }}>
                <label style={{ fontSize: '13px', color: '#666' }}>Or paste File / Cloud Storage URL (Optional fallback)</label>
                <input
                  type="text"
                  placeholder="https://drive.google.com/file/d/..."
                  value={documentData.fileUrl}
                  onChange={(e) => setDocumentData({ ...documentData, fileUrl: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>

              <button type="submit" className="btn-primary" style={{ marginTop: '15px' }}>
                📁 Upload Verification Document
              </button>
            </form>

            <div className="documents-list">
              <h4 style={{ marginBottom: '15px', color: '#333' }}>Uploaded Documents ({documents.length})</h4>
              {documents.length > 0 ? (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {documents.map((doc, idx) => {
                    const typeLabels = {
                      license: 'Medical License',
                      degree: 'Degree Certificate',
                      identity: 'Government ID Proof',
                      certificate: 'Specialization Certificate',
                      other: 'Credential Document'
                    };
                    const statusColors = {
                      approved: { bg: '#d1fae5', text: '#065f46', label: '✓ Approved' },
                      pending: { bg: '#fef3c7', text: '#92400e', label: '⏳ Pending Review' },
                      rejected: { bg: '#fee2e2', text: '#991b1b', label: '✗ Rejected' }
                    };
                    const statusInfo = statusColors[doc.reviewStatus || 'pending'] || statusColors.pending;

                    return (
                      <div key={doc._id || idx} style={{
                        padding: '16px 20px',
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        display: 'flex',
                        justify: 'space-between',
                        alignItems: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <span style={{ fontSize: '24px' }}>📄</span>
                          <div>
                            <div style={{ fontWeight: 600, color: '#1f2937', fontSize: '15px' }}>
                              {typeLabels[doc.documentType] || doc.documentType || 'Verification Document'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                              {doc.originalName ? `File: ${doc.originalName} • ` : ''}Uploaded: {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : 'Recently'}
                            </div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            backgroundColor: statusInfo.bg,
                            color: statusInfo.text
                          }}>
                            {statusInfo.label}
                          </span>
                          {doc.fileUrl && (
                            <a
                              href={doc.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                padding: '6px 12px',
                                background: '#f3eaff',
                                color: '#7c3aed',
                                borderRadius: '6px',
                                textDecoration: 'none',
                                fontSize: '13px',
                                fontWeight: 600
                              }}
                            >
                              👁️ View File
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ padding: '30px', textAlign: 'center', background: '#f9fafb', borderRadius: '8px', color: '#6b7280' }}>
                  <p style={{ margin: 0, fontSize: '15px' }}>No documents uploaded yet. Upload your credentials above to get verified.</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'bookings':
        return (
          <div className="physio-dashboard__card fade-in">
            <div className="section-header">
              <h3>Patient Bookings</h3>
            </div>
            {bookings.length > 0 ? (
              <div className="bookings-list" style={{ display: 'grid', gap: '16px' }}>
                {bookings.map(booking => {
                  const clientName = booking.clientId?.name || booking.clientName || 'Unknown Patient';
                  const clientEmail = booking.clientId?.email || 'N/A';
                  const clientPhone = booking.clientId?.phone || booking.clientPhone || '';
                  const scheduledDateStr = booking.scheduledAt ? new Date(booking.scheduledAt).toLocaleDateString() : 'N/A';

                  return (
                    <div key={booking._id} className="booking-card" style={{ padding: '20px', background: '#fff', border: booking.status === 'pending' ? '1.5px solid #bfdbfe' : '1px solid #e5e7eb', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      {/* 🔔 Prominent Pending Request Banner for Therapist */}
                      {booking.status === 'pending' && (
                        <div style={{
                          padding: '14px 18px',
                          background: 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)',
                          border: '1.5px solid #93c5fd',
                          borderRadius: '10px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '12px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '24px' }}>🔔</span>
                            <div>
                              <div style={{ fontWeight: 800, color: '#1e40af', fontSize: '14px' }}>
                                New Booking Request — Confirmation Required
                              </div>
                              <div style={{ color: '#3730a3', fontSize: '12px' }}>
                                Patient {clientName} is waiting. Before accepting, you can confirm the session, propose a reschedule, or decline.
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => updateBooking(booking._id, 'confirmed')}
                              style={{
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: '#ffffff',
                                padding: '8px 18px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '13px',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 3px 8px rgba(16,185,129,0.3)',
                              }}
                            >
                              ✓ Accept Booking
                            </button>
                            <button
                              onClick={() => {
                                setBookingActionModalTarget(booking);
                                setBookingActionModalType('reschedule');
                                setBookingActionModalOpen(true);
                              }}
                              style={{
                                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                                color: '#ffffff',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '13px',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 3px 8px rgba(124,58,237,0.3)',
                              }}
                            >
                              🔄 Reschedule Slot
                            </button>
                            <button
                              onClick={() => {
                                setBookingActionModalTarget(booking);
                                setBookingActionModalType('cancel');
                                setBookingActionModalOpen(true);
                              }}
                              style={{
                                background: '#ffffff',
                                color: '#dc2626',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '13px',
                                border: '1.5px solid #fca5a5',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 2px 5px rgba(220,38,38,0.1)',
                              }}
                            >
                              🚫 Decline / Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                        <div className="booking-info">
                          <h4 style={{ margin: '0 0 6px 0', fontSize: '16px', color: '#111827' }}>👤 Patient: {clientName}</h4>
                          <p style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#4b5563' }}>
                            📧 <strong>Email:</strong> {clientEmail} | 📞 <strong>Phone:</strong> {clientPhone || 'N/A'}
                          </p>
                          <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#6b7280' }}>
                            📅 <strong>Session Date:</strong> {scheduledDateStr} | 🕐 <strong>Mode:</strong> {booking.mode === 'online' ? '💻 Online' : '🏥 In-Person'}
                          </p>
                          <span className={`status-badge ${booking.status}`}>{booking.status}</span>
                        </div>

                      <div className="booking-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            setWhatsappTarget({
                              _id: booking.clientId?._id || booking.clientId,
                              name: clientName,
                              phone: clientPhone,
                              email: clientEmail,
                              role: 'client'
                            });
                            setInitialWhatsappMessage('');
                            setWhatsappModalOpen(true);
                          }}
                          style={{
                            background: '#00a884',
                            color: '#fff',
                            padding: '8px 16px',
                            borderRadius: '20px',
                            fontWeight: 700,
                            fontSize: '13px',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 3px 8px rgba(0,168,132,0.3)'
                          }}
                        >
                          💬 Inbuilt WhatsApp
                        </button>

                        <button
                          onClick={() => handleDownloadChatTranscript(booking.clientId?._id || booking.clientId, clientName)}
                          style={{
                            background: '#ffffff',
                            color: '#0f172a',
                            padding: '8px 14px',
                            borderRadius: '20px',
                            fontWeight: 700,
                            fontSize: '13px',
                            border: '1px solid #cbd5e1',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                          }}
                        >
                          📥 Download Chat
                        </button>

                        <button
                          onClick={() => handleSendChatToClient(booking.clientId?._id || booking.clientId, clientName)}
                          style={{
                            background: '#0284c7',
                            color: '#ffffff',
                            padding: '8px 14px',
                            borderRadius: '20px',
                            fontWeight: 700,
                            fontSize: '13px',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 2px 6px rgba(2,132,199,0.3)'
                          }}
                        >
                          📩 Send Chat to Client
                        </button>

                        {booking.status !== 'completed' && booking.status !== 'cancelled' && (
                          <button
                            onClick={() => handleEndSession(booking)}
                            style={{
                              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                              color: '#ffffff',
                              padding: '8px 16px',
                              borderRadius: '20px',
                              fontWeight: 700,
                              fontSize: '13px',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              boxShadow: '0 3px 8px rgba(239,68,68,0.35)'
                            }}
                          >
                            🏁 End Session
                          </button>
                        )}

                        {clientPhone && (
                          <a
                            href={buildWhatsAppLink(clientPhone, `Hello ${clientName}, I am reaching out regarding your therapy session booked on PsychMitra for ${scheduledDateStr}.`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              background: '#25D366',
                              color: '#fff',
                              padding: '8px 16px',
                              borderRadius: '20px',
                              fontWeight: 700,
                              fontSize: '13px',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              boxShadow: '0 3px 8px rgba(37,211,102,0.3)'
                            }}
                          >
                            📲 External App
                          </a>
                        )}

                        {booking.status === 'pending' && (
                          <>
                            <button
                              className="btn-success"
                              onClick={() => updateBooking(booking._id, 'confirmed')}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 700 }}
                            >
                              ✓ Accept
                            </button>
                            <button
                              onClick={() => {
                                setBookingActionModalTarget(booking);
                                setBookingActionModalType('reschedule');
                                setBookingActionModalOpen(true);
                              }}
                              style={{
                                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                                color: '#ffffff',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontWeight: 700,
                                fontSize: '13px',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 3px 8px rgba(124,58,237,0.3)'
                              }}
                            >
                              🔄 Reschedule
                            </button>
                            <button
                              className="btn-danger"
                              onClick={() => {
                                setBookingActionModalTarget(booking);
                                setBookingActionModalType('cancel');
                                setBookingActionModalOpen(true);
                              }}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 700 }}
                            >
                              🚫 Decline / Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Reschedule or Cancellation History Notes */}
                    {booking.rescheduleReason && (
                        <div style={{
                          width: '100%',
                          marginTop: '10px',
                          padding: '10px 14px',
                          background: '#f5f3ff',
                          border: '1px solid #ddd6fe',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#5b21b6'
                        }}>
                          🔄 <strong>Reschedule Note ({booking.rescheduledBy === 'physiotherapist' || booking.rescheduledBy === 'therapist' ? 'Rescheduled by You' : booking.rescheduledBy === 'client' ? 'Requested by Patient' : booking.rescheduledBy || 'Updated'}):</strong> {booking.rescheduleReason}
                        </div>
                      )}

                      {booking.cancellationReason && (
                        <div style={{
                          width: '100%',
                          marginTop: '10px',
                          padding: '10px 14px',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#991b1b'
                        }}>
                          🚫 <strong>Cancellation Reason ({booking.cancelledBy === 'physiotherapist' || booking.cancelledBy === 'therapist' ? 'Cancelled by You' : booking.cancelledBy === 'client' ? 'Cancelled by Patient' : booking.cancelledBy || 'Cancelled'}):</strong> {booking.cancellationReason}
                        </div>
                      )}

                      {booking.review && (
                        <div style={{
                          width: '100%',
                          marginTop: '12px',
                          background: '#fffbeb',
                          border: '1px solid #fde68a',
                          borderRadius: '12px',
                          padding: '12px 16px'
                        }}>
                          <div style={{ fontWeight: 700, color: '#b45309', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>⭐ Client Feedback & Rating:</span>
                            <span style={{ color: '#d97706', fontSize: '15px' }}>
                              {'★'.repeat(booking.review.rating || 5)}{'☆'.repeat(5 - (booking.review.rating || 5))} ({booking.review.rating}/5)
                            </span>
                          </div>
                          {booking.review.comment && (
                            <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#92400e', fontStyle: 'italic' }}>
                              "{booking.review.comment}"
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-data">No patient bookings to show.</div>
            )
            }
          </div >
        );
      case 'blogs':
        return (
          <div className="physio-dashboard__card fade-in">
            <div className="section-header" style={{ marginBottom: '24px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  ✍️ Clinical Insights & Blog Management
                </h3>
                <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>
                  Create and publish articles to guide patients, share wellness tips, and enhance mental healthcare awareness.
                </p>
              </div>
            </div>

            {/* CREATE BLOG FORM MATCHING ADMIN SECTION */}
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
              marginBottom: '32px'
            }}>
              <h4 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                ➕ Create New Blog Article
              </h4>

              <form onSubmit={handleCreateBlog}>
                {/* Article Title */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                    Article Title *
                  </label>
                  <input
                    type="text"
                    value={blogForm.title}
                    onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                    placeholder="Enter blog article title (e.g. 5 Grounding Techniques for Anxiety)"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      outline: 'none'
                    }}
                  />
                </div>

                {/* 2-Columns: Author Name & Category */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                      Author Name
                    </label>
                    <input
                      type="text"
                      value={blogForm.author}
                      onChange={(e) => setBlogForm({ ...blogForm, author: e.target.value })}
                      placeholder="Author name (e.g. Dr. John Doe)"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                      Category *
                    </label>
                    <select
                      value={blogForm.category}
                      onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        background: '#fff',
                        outline: 'none'
                      }}
                    >
                      {blogCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Cover Image URL & File Upload */}
                <div style={{ marginBottom: '16px', background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                    Cover Image
                  </label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={blogForm.featuredImage}
                      onChange={(e) => setBlogForm({ ...blogForm, featuredImage: e.target.value })}
                      placeholder="https://images.unsplash.com/... or upload image"
                      style={{
                        flex: 1,
                        minWidth: '220px',
                        padding: '9px 12px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        background: '#fff',
                        outline: 'none'
                      }}
                    />

                    <label
                      style={{
                        padding: '9px 14px',
                        background: '#ede9fe',
                        color: '#6d28d9',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '13px',
                        cursor: uploadingBlogImage ? 'not-allowed' : 'pointer',
                        border: '1px solid #ddd6fe',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleBlogImageUpload(e, false)}
                        disabled={uploadingBlogImage}
                        style={{ display: 'none' }}
                      />
                      {uploadingBlogImage ? '⏳ Uploading...' : '📁 Upload Image'}
                    </label>

                    {blogForm.featuredImage && (
                      <button
                        type="button"
                        onClick={() => setBlogForm({ ...blogForm, featuredImage: '' })}
                        style={{
                          padding: '9px 12px',
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: '1px solid #fecaca',
                          borderRadius: '6px',
                          fontWeight: 600,
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        ✕ Clear
                      </button>
                    )}
                  </div>

                  {blogForm.featuredImage && (
                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <img
                        src={getMediaUrl(blogForm.featuredImage)}
                        alt="Cover Preview"
                        onError={(e) => { e.target.style.display = 'none'; }}
                        style={{
                          width: '80px',
                          height: '50px',
                          objectFit: 'cover',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1'
                        }}
                      />
                      <span style={{ fontSize: '12px', color: '#64748b' }}>
                        Image preview loaded.
                      </span>
                    </div>
                  )}
                </div>

                {/* Excerpt / Brief Summary */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                    Excerpt / Brief Summary *
                  </label>
                  <textarea
                    value={blogForm.excerpt}
                    onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                    placeholder="Brief summary displayed on article cards..."
                    required
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                      outline: 'none',
                      lineHeight: '1.5'
                    }}
                  />
                </div>

                {/* Full Article Content */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                    Full Article Content *
                  </label>
                  <textarea
                    value={blogForm.content}
                    onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
                    placeholder="Write full article body text here..."
                    required
                    rows="6"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                      outline: 'none',
                      lineHeight: '1.6'
                    }}
                  />
                </div>

                {/* Form Buttons */}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setBlogForm({
                      title: '',
                      author: authSession?.user?.name ? `Dr. ${authSession.user.name}` : '',
                      category: blogCategories[0] || 'Mental Health',
                      excerpt: '',
                      content: '',
                      featuredImage: ''
                    })}
                    style={{
                      padding: '10px 18px',
                      background: '#f1f5f9',
                      color: '#475569',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Reset Form
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '10px 24px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '14px',
                      boxShadow: '0 4px 12px rgba(102,126,234,0.3)'
                    }}
                  >
                    📝 Publish Article
                  </button>
                </div>
              </form>
            </div>

            {/* Published Blogs List */}
            <h4 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
              📚 My Submitted Articles ({blogs.length})
            </h4>

            {blogs.length > 0 ? (
              <div className="items-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '18px' }}>
                {blogs.map(blog => {
                  const bId = blog._id || blog.id;
                  const isApproved = blog.status === 'approved' || blog.status === 'published';
                  const isRejected = blog.status === 'rejected';
                  const dateStr = blog.createdAt ? new Date(blog.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recent';

                  return (
                    <div key={bId} className="item-card" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '18px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                      <div>
                        {blog.featuredImage && (
                          <img
                            src={getMediaUrl(blog.featuredImage)}
                            alt={blog.title}
                            onError={(e) => { e.target.style.display = 'none'; }}
                            style={{
                              width: '100%',
                              height: '140px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              marginBottom: '12px',
                              border: '1px solid #e2e8f0'
                            }}
                          />
                        )}

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '3px 8px',
                            borderRadius: '10px',
                            background: isApproved ? '#d1fae5' : isRejected ? '#fee2e2' : '#fef3c7',
                            color: isApproved ? '#047857' : isRejected ? '#b91c1c' : '#b45309'
                          }}>
                            {isApproved ? '✓ Live / Approved' : isRejected ? '✕ Rejected' : '⏳ Pending Review'}
                          </span>
                          <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                            {blog.category || 'Mental Health'}
                          </span>
                        </div>

                        <h4 style={{ fontSize: '16px', fontWeight: 700, margin: '8px 0 6px 0', color: '#0f172a' }}>{blog.title}</h4>

                        <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>
                          📅 {dateStr}
                        </div>

                        <p style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5', margin: '0 0 14px 0' }}>
                          {blog.excerpt || blog.content?.substring(0, 110)}...
                        </p>

                        {blog.reviewNote && (
                          <div style={{ padding: '6px 10px', background: isRejected ? '#fff1f2' : '#f0fdf4', borderRadius: '6px', border: `1px solid ${isRejected ? '#fecdd3' : '#bbf7d0'}`, fontSize: '12px', color: isRejected ? '#9f1239' : '#166534', marginBottom: '12px' }}>
                            <strong>💬 Review Note:</strong> {blog.reviewNote}
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                        <button
                          className="btn-small"
                          onClick={() => {
                            setEditBlogId(bId);
                            setEditBlogForm({
                              title: blog.title || '',
                              author: blog.createdBy?.name || blog.author || (authSession?.user?.name ? `Dr. ${authSession.user.name}` : ''),
                              category: blog.category || 'Mental Health',
                              excerpt: blog.excerpt || '',
                              content: blog.content || '',
                              featuredImage: blog.featuredImage || blog.image || ''
                            });
                          }}
                          style={{ background: '#6366f1', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12.5px', fontWeight: 600, flex: 1 }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          className="btn-small-danger"
                          onClick={() => handleDeleteBlog(bId)}
                          style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '12.5px', fontWeight: 600 }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-data">Share your clinical insights and mental wellness guides by creating a new article above.</div>
            )}

            {/* EDIT BLOG MODAL MATCHING ADMIN SECTION */}
            {editBlogId && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px'
              }}>
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '30px',
                  maxWidth: '650px',
                  width: '90%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h4 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
                      ✏️ Edit Blog Article
                    </h4>
                    <button
                      onClick={() => setEditBlogId(null)}
                      style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}
                    >
                      ✕
                    </button>
                  </div>

                  <form onSubmit={handleUpdateBlog}>
                    {/* Article Title */}
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333', fontSize: '14px' }}>
                        Article Title *
                      </label>
                      <input
                        type="text"
                        required
                        value={editBlogForm.title}
                        onChange={(e) => setEditBlogForm({ ...editBlogForm, title: e.target.value })}
                        placeholder="Enter blog article title"
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    {/* 2-Columns: Author Name & Category */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333', fontSize: '14px' }}>
                          Author Name
                        </label>
                        <input
                          type="text"
                          value={editBlogForm.author}
                          onChange={(e) => setEditBlogForm({ ...editBlogForm, author: e.target.value })}
                          placeholder="Author name (e.g. Dr. John)"
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333', fontSize: '14px' }}>
                          Category *
                        </label>
                        <select
                          value={editBlogForm.category}
                          onChange={(e) => setEditBlogForm({ ...editBlogForm, category: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                            background: '#fff'
                          }}
                        >
                          {blogCategories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Cover Image URL & File Upload */}
                    <div style={{ marginBottom: '16px', background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333', fontSize: '14px' }}>
                        Cover Image URL
                      </label>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
                        <input
                          type="text"
                          value={editBlogForm.featuredImage}
                          onChange={(e) => setEditBlogForm({ ...editBlogForm, featuredImage: e.target.value })}
                          placeholder="https://images.unsplash.com/... or upload image"
                          style={{
                            flex: 1,
                            minWidth: '200px',
                            padding: '9px 10px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                            background: '#fff'
                          }}
                        />

                        <label
                          style={{
                            padding: '9px 14px',
                            background: '#ede9fe',
                            color: '#6d28d9',
                            borderRadius: '6px',
                            fontWeight: 600,
                            fontSize: '13px',
                            cursor: uploadingEditBlogImage ? 'not-allowed' : 'pointer',
                            border: '1px solid #ddd6fe',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleBlogImageUpload(e, true)}
                            disabled={uploadingEditBlogImage}
                            style={{ display: 'none' }}
                          />
                          {uploadingEditBlogImage ? '⏳ Uploading...' : '📁 Upload Image'}
                        </label>

                        {editBlogForm.featuredImage && (
                          <button
                            type="button"
                            onClick={() => setEditBlogForm({ ...editBlogForm, featuredImage: '' })}
                            style={{
                              padding: '9px 12px',
                              background: '#fee2e2',
                              color: '#dc2626',
                              border: '1px solid #fecaca',
                              borderRadius: '6px',
                              fontWeight: 600,
                              fontSize: '13px',
                              cursor: 'pointer'
                            }}
                          >
                            ✕ Clear
                          </button>
                        )}
                      </div>

                      {editBlogForm.featuredImage && (
                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <img
                            src={getMediaUrl(editBlogForm.featuredImage)}
                            alt="Cover Preview"
                            onError={(e) => { e.target.style.display = 'none'; }}
                            style={{
                              width: '80px',
                              height: '50px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1'
                            }}
                          />
                          <span style={{ fontSize: '12px', color: '#64748b' }}>
                            Image preview loaded.
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Excerpt / Brief Summary */}
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333', fontSize: '14px' }}>
                        Excerpt / Brief Summary *
                      </label>
                      <textarea
                        required
                        rows="3"
                        value={editBlogForm.excerpt}
                        onChange={(e) => setEditBlogForm({ ...editBlogForm, excerpt: e.target.value })}
                        placeholder="Brief summary displayed on article cards..."
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>

                    {/* Full Article Content */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#333', fontSize: '14px' }}>
                        Full Article Content
                      </label>
                      <textarea
                        rows="6"
                        value={editBlogForm.content}
                        onChange={(e) => setEditBlogForm({ ...editBlogForm, content: e.target.value })}
                        placeholder="Write full article body text here..."
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>

                    {/* Form Action Buttons */}
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => setEditBlogId(null)}
                        style={{
                          padding: '10px 20px',
                          background: '#f0f0f0',
                          color: '#333',
                          border: 'none',
                          borderRadius: '6px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        style={{
                          padding: '10px 20px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Update Article
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        );
      case 'notes': {
        const bookedClients = Array.from(
          new Map(
            bookings
              .map(b => {
                const cName = b.clientId?.name || b.clientName;
                const cId = b.clientId?._id || b.clientId || null;
                const cEmail = b.clientId?.email || b.clientEmail || '';
                const cPhone = b.clientId?.phone || b.clientPhone || '';
                if (!cName) return null;
                return { id: cId, name: cName.trim(), email: cEmail, phone: cPhone };
              })
              .filter(Boolean)
              .map(c => [c.name, c])
          ).values()
        );

        return (
          <div className="physio-dashboard__card fade-in" style={{ padding: '24px' }}>
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', color: '#111827', fontWeight: 800 }}>📋 Client Session Notes & Collaborative Worksheets</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
                  Record clinical notes for your clients. Optionally attach an interactive worksheet or Google Sheet link which will be shared with real-time live synchronization.
                </p>
              </div>
              {notes.length > 0 && (
                <button
                  type="button"
                  onClick={handleDownloadAllNotes}
                  style={{
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    color: '#ffffff',
                    padding: '9px 18px',
                    borderRadius: '20px',
                    fontWeight: 700,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 3px 8px rgba(5,150,105,0.3)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  📥 Export All Notes Summary
                </button>
              )}
            </div>

            {/* Create Note Form */}
            <form onSubmit={handleCreateNote} className="physio-form" style={{ marginBottom: '30px', padding: '22px', background: '#f8fafc', borderRadius: '14px', border: '1px solid #cbd5e1' }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#0f172a', fontWeight: 700 }}>📝 Add New Session Note & Worksheet</h4>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155', display: 'block', marginBottom: '6px' }}>
                  Select Client from Bookings *
                </label>
                {bookedClients.length > 0 ? (
                  <select
                    value={noteForm.clientName}
                    onChange={(e) => {
                      const val = e.target.value;
                      const matched = bookedClients.find(c => c.name === val);
                      setNoteForm({
                        ...noteForm,
                        clientName: val,
                        clientId: matched ? matched.id : '',
                        clientEmail: matched ? matched.email : '',
                      });
                    }}
                    required
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff' }}
                  >
                    <option value="">-- Select Client ({bookedClients.length} booked client{bookedClients.length > 1 ? 's' : ''}) --</option>
                    {bookedClients.map((client, idx) => (
                      <option key={idx} value={client.name}>
                        👤 {client.name} {client.phone ? `(${client.phone})` : ''}
                      </option>
                    ))}
                    <option value="__custom__">✏️ Enter Custom Client Name...</option>
                  </select>
                ) : (
                  <div style={{ fontSize: '13px', color: '#b45309', background: '#fffbeb', padding: '10px 14px', borderRadius: '8px', border: '1px solid #fde68a', marginBottom: '10px' }}>
                    ℹ️ No client bookings found yet. You can manually enter the client's name below.
                  </div>
                )}

                {/* Custom Client Name input */}
                {(bookedClients.length === 0 || noteForm.clientName === '__custom__') && (
                  <input
                    type="text"
                    value={noteForm.clientName === '__custom__' ? '' : noteForm.clientName}
                    onChange={(e) => setNoteForm({ ...noteForm, clientName: e.target.value, clientId: '', clientEmail: '' })}
                    placeholder="Enter client full name..."
                    required
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', marginTop: '10px' }}
                  />
                )}
              </div>

              <div className="form-group" style={{ marginBottom: '18px' }}>
                <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155', display: 'block', marginBottom: '6px' }}>
                  Session Note / Observations *
                </label>
                <textarea
                  value={noteForm.note}
                  onChange={(e) => setNoteForm({ ...noteForm, note: e.target.value })}
                  placeholder="Record clinical observations, diagnostic notes, discussion summary, or homework instructions..."
                  rows="4"
                  required
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontFamily: 'inherit' }}
                ></textarea>
              </div>

              {/* SINGLE OPTIONAL WORKSHEET FIELD (FETCHED FROM ADMIN) */}
              <div className="form-group" style={{ marginBottom: '18px' }}>
                <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155', display: 'block', marginBottom: '6px' }}>
                  Attach Worksheet <span style={{ fontWeight: 400, color: '#64748b' }}>(Optional)</span>
                </label>
                <select
                  value={noteForm.selectedWorksheetId}
                  onChange={(e) => setNoteForm({ ...noteForm, selectedWorksheetId: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff' }}
                >
                  <option value="">-- None (Private Session Note, Not Shared with Client) --</option>
                  {availableWorksheets.map((ws) => (
                    <option key={ws._id} value={ws._id}>
                      📑 {ws.title}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                  {noteForm.selectedWorksheetId ? (
                    <span style={{ color: '#166534', fontWeight: 600, background: '#f0fdf4', padding: '4px 8px', borderRadius: '6px', display: 'inline-block' }}>
                      🟢 Worksheet selected. This will be sent to client's "My Worksheets" with live real-time sync.
                    </span>
                  ) : (
                    <span>🔒 If no worksheet is selected, this note remains private for therapist use only.</span>
                  )}
                </div>
              </div>

              <button type="submit" className="btn-primary" style={{ background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', color: '#fff', padding: '11px 26px', borderRadius: '8px', border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: '14px' }}>
                💾 Save Session Note {noteForm.selectedWorksheetId ? '& Share Worksheet' : ''}
              </button>
            </form>

            {/* Published Notes List */}
            <h4 style={{ marginBottom: '16px', color: '#0f172a', fontSize: '16px', fontWeight: 700 }}>Saved Client Notes ({notes.length})</h4>
            {notes.length > 0 ? (
              <div className="items-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '18px' }}>
                {notes.map(note => {
                  const nId = note._id || note.id;
                  const ws = note.worksheet || {};
                  const hasWorksheet = Boolean(ws.hasWorksheet);

                  return (
                    <div key={nId} className="item-card" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <h4 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: '#0f172a' }}>👤 {note.clientName}</h4>
                          <span style={{ fontSize: '11.5px', color: '#64748b', background: '#f1f5f9', padding: '2px 8px', borderRadius: '6px' }}>
                            {note.createdAt ? new Date(note.createdAt).toLocaleDateString() : 'Recent'}
                          </span>
                        </div>

                        {/* Visibility Status Badge */}
                        <div style={{ marginBottom: '10px' }}>
                          {hasWorksheet ? (
                            <span style={{ fontSize: '11px', fontWeight: 700, background: '#dcfce7', color: '#15803d', padding: '3px 8px', borderRadius: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              🟢 Shared with Client (Worksheet: {ws.title || 'Attached'})
                            </span>
                          ) : (
                            <span style={{ fontSize: '11px', fontWeight: 600, background: '#f1f5f9', color: '#64748b', padding: '3px 8px', borderRadius: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              🔒 Private Note (Therapist Only)
                            </span>
                          )}
                        </div>

                        <p style={{ fontSize: '13.5px', color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-wrap', margin: '0 0 14px 0' }}>
                          {note.note}
                        </p>

                        {/* Attached Worksheet Preview Box */}
                        {hasWorksheet && (
                          <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', border: '1px solid #e2e8f0', marginBottom: '14px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                              📑 {ws.title || 'Attached Clinical Worksheet'}
                            </div>
                            <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                              Real-Time Collaborative Sheet (Live updates sync between client & therapist)
                            </div>
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                        {/* Open Live Sheet Modal if worksheet attached */}
                        {hasWorksheet && (
                          <button
                            className="btn-small"
                            onClick={() => setSelectedLiveSheetNote(note)}
                            style={{ background: '#10b981', color: '#fff', border: 'none', padding: '7px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px', width: '100%', justifyContent: 'center', marginBottom: '4px' }}
                          >
                            📊 Open Live Worksheet (Collaborate)
                          </button>
                        )}

                        <button
                          className="btn-small"
                          onClick={() => handleDownloadSingleNote(note)}
                          style={{ background: '#059669', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', flex: 1, justifyContent: 'center' }}
                        >
                          📥 Note
                        </button>
                        <button
                          className="btn-small"
                          onClick={() => {
                            setEditNoteId(nId);
                            setEditNoteForm({
                              clientName: note.clientName || '',
                              clientId: note.clientId?._id || note.clientId || '',
                              clientEmail: note.clientEmail || '',
                              note: note.note || '',
                              selectedWorksheetId: ws.hasWorksheet ? (ws.worksheetId?._id || ws.worksheetId || '') : '',
                            });
                          }}
                          style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', flex: 1, justifyContent: 'center' }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          className="btn-small-danger"
                          onClick={() => handleDeleteNote(nId)}
                          style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', flex: 1, justifyContent: 'center' }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-data">No diagnostic or session notes created yet. Use the form above to add a note for a client.</div>
            )}

            {/* EDIT NOTE MODAL */}
            {editNoteId && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(15, 23, 42, 0.65)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 99999,
                padding: '20px'
              }}>
                <div style={{ background: '#fff', borderRadius: '16px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '24px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>✏️ Edit Session Note & Worksheet</h3>
                    <button onClick={() => setEditNoteId(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>✕</button>
                  </div>
                  <form onSubmit={handleUpdateNote} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Client Name *</label>
                      {bookedClients.length > 0 ? (
                        <select
                          value={editNoteForm.clientName}
                          onChange={(e) => {
                            const val = e.target.value;
                            const matched = bookedClients.find(c => c.name === val);
                            setEditNoteForm({
                              ...editNoteForm,
                              clientName: val,
                              clientId: matched ? matched.id : '',
                              clientEmail: matched ? matched.email : '',
                            });
                          }}
                          required
                          style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px', background: '#fff' }}
                        >
                          <option value="">-- Select Client from Bookings --</option>
                          {bookedClients.map((client, idx) => (
                            <option key={idx} value={client.name}>
                              👤 {client.name} {client.phone ? `(${client.phone})` : ''}
                            </option>
                          ))}
                          <option value="__custom__">✏️ Custom Client Name...</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          required
                          value={editNoteForm.clientName}
                          onChange={(e) => setEditNoteForm({ ...editNoteForm, clientName: e.target.value })}
                          style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px' }}
                        />
                      )}

                      {editNoteForm.clientName === '__custom__' && (
                        <input
                          type="text"
                          value=""
                          onChange={(e) => setEditNoteForm({ ...editNoteForm, clientName: e.target.value })}
                          placeholder="Enter client full name..."
                          required
                          style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px', marginTop: '8px' }}
                        />
                      )}
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Session Note *</label>
                      <textarea
                        required
                        rows="4"
                        value={editNoteForm.note}
                        onChange={(e) => setEditNoteForm({ ...editNoteForm, note: e.target.value })}
                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px', fontFamily: 'inherit' }}
                      />
                    </div>

                    {/* Edit Worksheet Selector */}
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
                        Attach Worksheet (Optional)
                      </label>
                      <select
                        value={editNoteForm.selectedWorksheetId}
                        onChange={(e) => setEditNoteForm({ ...editNoteForm, selectedWorksheetId: e.target.value })}
                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13.5px', background: '#fff' }}
                      >
                        <option value="">-- None (Private Session Note, Not Shared with Client) --</option>
                        {availableWorksheets.map((ws) => (
                          <option key={ws._id} value={ws._id}>
                            📑 {ws.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                      <button type="button" onClick={() => setEditNoteId(null)} style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                      <button type="submit" style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>Update Note</button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* LIVE COLLABORATIVE WORKSHEET MODAL (THERAPIST VIEW) */}
            {selectedLiveSheetNote && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(15, 23, 42, 0.75)',
                  zIndex: 999999,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '24px',
                  backdropFilter: 'blur(4px)',
                }}
              >
                <div
                  style={{
                    width: '100%',
                    maxWidth: '1050px',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    borderRadius: '16px',
                  }}
                >
                  <LiveCollaborativeSheet
                    noteId={selectedLiveSheetNote._id}
                    initialNote={selectedLiveSheetNote}
                    currentUserRole="physiotherapist"
                    onClose={() => {
                      setSelectedLiveSheetNote(null);
                      clientNoteApi.getNotes().then(data => setNotes(data.notes || data || []));
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      }
      case 'events': {
        const isFirstFree = Boolean(eventEligibility.isFirstFreeEligible);
        const plans = eventEligibility.plans || [];

        return (
          <div className="physio-dashboard__card fade-in" style={{ padding: '24px' }}>
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', color: '#111827', fontWeight: 800 }}>🎉 Host Seminars & Workshops</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#6b7280' }}>Create, manage and publish your mental health events, webinars and seminars.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowEventForm(!showEventForm)}
                style={{
                  background: showEventForm
                    ? 'linear-gradient(135deg, #64748b 0%, #475569 100%)'
                    : 'linear-gradient(135deg, #00a884 0%, #059669 100%)',
                  color: '#ffffff',
                  padding: '10px 20px',
                  borderRadius: '24px',
                  fontWeight: 700,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13.5px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  margin: 0,
                  boxShadow: showEventForm
                    ? '0 4px 12px rgba(100, 116, 139, 0.3)'
                    : '0 4px 14px rgba(0, 168, 132, 0.35)',
                  transition: 'all 0.25s ease',
                  whiteSpace: 'nowrap',
                  height: 'fit-content'
                }}
              >
                {showEventForm ? (
                  <>
                    <span style={{ fontSize: '14px', lineHeight: 1 }}>✕</span> Close Form
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: '15px', lineHeight: 1 }}>➕</span> Host New Event
                  </>
                )}
              </button>
            </div>

            {/* Create Event Form Card */}
            {showEventForm && (
              <form onSubmit={handleCreateEvent} className="physio-form" style={{ marginBottom: '28px', padding: '22px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #cbd5e1', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#0f172a', fontWeight: 700 }}>Host New Event or Seminar</h4>

                {/* Pro 1st Event Free Banner OR Plan Selection */}
                {isFirstFree ? (
                  <div style={{
                    padding: '16px 20px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    border: '1.5px solid #f59e0b',
                    color: '#78350f',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)',
                  }}>
                    <span style={{ fontSize: '32px' }}>🎁</span>
                    <div>
                      <strong style={{ fontSize: '15px', display: 'block', color: '#92400e', marginBottom: '2px' }}>
                        Pro Member Privilege: Your 1st Event is 100% FREE for 1 Full Week!
                      </strong>
                      <p style={{ margin: 0, fontSize: '12.5px', color: '#78350f', lineHeight: 1.4 }}>
                        As an active Pro therapist member, your first seminar/workshop listing is automatically published free of charge for 7 days in the platform public directory. Once this 1-week free showcase concludes, you can extend or purchase publish plans according to your requirements.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginBottom: '22px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <label style={{ fontWeight: 700, fontSize: '13.5px', color: '#1e293b', margin: 0 }}>
                        💳 Choose an Event Publish Plan:
                      </label>
                      <span style={{ fontSize: '12px', color: '#64748b' }}>Select directory visibility duration</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '12px' }}>
                      {plans.map((plan) => {
                        const isSelected = selectedPlanId === plan._id;
                        return (
                          <div
                            key={plan._id}
                            onClick={() => setSelectedPlanId(plan._id)}
                            style={{
                              padding: '14px',
                              borderRadius: '10px',
                              border: isSelected ? '2px solid #7c3aed' : '1px solid #cbd5e1',
                              background: isSelected ? '#f5f3ff' : '#ffffff',
                              cursor: 'pointer',
                              position: 'relative',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? '0 4px 12px rgba(124, 58, 237, 0.12)' : 'none',
                            }}
                          >
                            {plan.badge && (
                              <span style={{
                                position: 'absolute',
                                top: '-9px',
                                right: '12px',
                                background: isSelected ? '#7c3aed' : '#64748b',
                                color: '#fff',
                                fontSize: '10px',
                                fontWeight: 800,
                                padding: '2px 8px',
                                borderRadius: '10px',
                                textTransform: 'uppercase',
                              }}>
                                {plan.badge}
                              </span>
                            )}
                            <div style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a', marginBottom: '4px' }}>
                              {plan.name}
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: 800, color: '#7c3aed', marginBottom: '4px' }}>
                              ₹{plan.price}
                              <span style={{ fontSize: '12px', fontWeight: 500, color: '#64748b' }}> / {plan.weeks} wk{plan.weeks > 1 ? 's' : ''}</span>
                            </div>
                            <p style={{ margin: '0 0 6px 0', fontSize: '11.5px', color: '#64748b', lineHeight: 1.3 }}>
                              {plan.description || `${plan.durationDays || plan.weeks * 7} Days directory visibility`}
                            </p>
                            <div style={{ fontSize: '11px', color: '#059669', fontWeight: 600 }}>
                              ✓ Active for {plan.durationDays || plan.weeks * 7} Days
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                  <div className="form-group">
                    <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>Event Title *</label>
                    <input
                      type="text"
                      value={eventForm.title}
                      onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                      placeholder="e.g. Anxiety Management & Mindfulness Workshop"
                      required
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>Event Category</label>
                    <select
                      value={eventForm.eventType}
                      onChange={(e) => setEventForm({ ...eventForm, eventType: e.target.value })}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                    >
                      <option value="seminar">🎤 Seminar</option>
                      <option value="workshop">🛠️ Workshop</option>
                      <option value="event">🎉 Special Event</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>Session Mode</label>
                    <select
                      value={eventForm.mode}
                      onChange={(e) => setEventForm({ ...eventForm, mode: e.target.value })}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                    >
                      <option value="online">💻 Online (Google Meet / Web Room)</option>
                      <option value="offline">🏥 Offline (In-Person Clinic)</option>
                    </select>
                  </div>

                  {eventForm.mode === 'offline' && (
                    <div className="form-group">
                      <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>Clinic / Venue Address</label>
                      <input
                        type="text"
                        value={eventForm.location}
                        onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                        placeholder="Clinic address or hall location..."
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                      />
                    </div>
                  )}
                </div>

                {/* Client Purpose: Event Fee and Registration Link */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px', marginBottom: '16px' }}>
                  <div className="form-group">
                    <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>🎟️</span> Event Fee (for Clients/Attendees)
                    </label>
                    <input
                      type="text"
                      value={eventForm.eventFee}
                      onChange={(e) => setEventForm({ ...eventForm, eventFee: e.target.value })}
                      placeholder="e.g. Free, ₹499, ₹999 / person"
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                    />
                    <span style={{ fontSize: '11px', color: '#64748b', marginTop: '3px', display: 'block' }}>Informational for clients (e.g. Free or entry fee)</span>
                  </div>

                  <div className="form-group">
                    <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>🔗</span> Client Registration Link (URL)
                    </label>
                    <input
                      type="url"
                      value={eventForm.registrationLink}
                      onChange={(e) => setEventForm({ ...eventForm, registrationLink: e.target.value })}
                      placeholder="https://forms.gle/... or https://zoom.us/..."
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                    />
                    <span style={{ fontSize: '11px', color: '#64748b', marginTop: '3px', display: 'block' }}>External form, Zoom register, or booking link for attendees</span>
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>Event Summary & Description</label>
                  <textarea
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    placeholder="Provide details about key topics covered, eligibility, or preparation required..."
                    rows="3"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                  ></textarea>
                </div>

                {/* Video / File Upload Section */}
                <div style={{ marginBottom: '18px', padding: '14px 16px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #cbd5e1' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontWeight: 700, fontSize: '13px', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>🎥</span> Upload Event Video / Media File
                    </label>
                    {uploadingEventVideo && <span style={{ fontSize: '12px', color: '#059669', fontWeight: 700 }}>⏳ Uploading Video...</span>}
                  </div>

                  {eventVideoError && (
                    <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '12px', marginBottom: '10px' }}>
                      ⚠️ {eventVideoError}
                    </div>
                  )}

                  {eventForm.videoUrl ? (
                    <div style={{ background: '#ffffff', padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #a7f3d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}>🎬</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: '#065f46' }}>{eventForm.videoFileName || 'Attached Video'}</div>
                          <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                            {eventForm.videoFileSize ? `${(eventForm.videoFileSize / (1024 * 1024)).toFixed(2)} MB • ` : ''}
                            <a href={eventForm.videoUrl} target="_blank" rel="noreferrer" style={{ color: '#0284c7', textDecoration: 'underline', fontWeight: 600 }}>
                              Preview Video File ↗
                            </a>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveEventVideo(false)}
                        style={{ padding: '4px 10px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '6px', fontSize: '11.5px', fontWeight: 700, cursor: 'pointer' }}
                      >
                        ✕ Remove
                      </button>
                    </div>
                  ) : (
                    <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #cbd5e1', borderRadius: '8px', padding: '14px', background: '#ffffff', cursor: uploadingEventVideo ? 'not-allowed' : 'pointer', textAlign: 'center' }}>
                      <input type="file" accept="video/*,.mp4,.mov,.webm,.mkv,.avi,.pdf" onChange={(e) => handleEventVideoUpload(e, false)} disabled={uploadingEventVideo} style={{ display: 'none' }} />
                      <span style={{ fontSize: '22px', marginBottom: '4px' }}>📁</span>
                      <span style={{ fontWeight: 700, color: '#475569', fontSize: '13px' }}>{uploadingEventVideo ? 'Uploading Video File...' : 'Click or Drag & Drop to Upload Video (.mp4, .mov, etc.)'}</span>
                      <span style={{ fontSize: '11.5px', color: '#94a3b8', marginTop: '2px' }}>Supports video files up to 200MB</span>
                    </label>
                  )}
                </div>

                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>Start Date & Time *</label>
                  <input
                    type="datetime-local"
                    value={eventForm.scheduledAt}
                    onChange={(e) => setEventForm({ ...eventForm, scheduledAt: e.target.value })}
                    required
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                  />
                  {eventForm.scheduledAt && (
                    <div style={{ marginTop: '8px', padding: '8px 12px', background: '#ecfdf5', borderRadius: '8px', border: '1px solid #a7f3d0', fontSize: '12px', color: '#065f46', fontWeight: 600 }}>
                      ✨ End Date & Publish Validity: Automatically decided as per {isFirstFree ? '1-Week Free Trial (7 Days)' : (plans.find(p => p._id === selectedPlanId)?.name || 'selected plan')} from {new Date(eventForm.scheduledAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}.
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <button
                    type="submit"
                    disabled={isProcessingEvent}
                    style={{
                      background: isFirstFree
                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                        : 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                      color: '#ffffff',
                      padding: '11px 26px',
                      borderRadius: '24px',
                      fontWeight: 700,
                      border: 'none',
                      cursor: isProcessingEvent ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
                      transition: 'all 0.2s ease',
                      margin: 0
                    }}
                  >
                    {isProcessingEvent ? '⏳ Processing...' : (isFirstFree ? '🎁 Publish FREE for 1 Week' : '💳 Pay & Publish Event')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowEventForm(false)}
                    style={{
                      background: '#ffffff',
                      color: '#64748b',
                      padding: '11px 20px',
                      borderRadius: '24px',
                      fontWeight: 600,
                      border: '1px solid #cbd5e1',
                      cursor: 'pointer',
                      fontSize: '13.5px',
                      transition: 'all 0.2s ease',
                      margin: 0
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* EXTEND EVENT MODAL */}
            {extendModal.isOpen && extendModal.event && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(15, 23, 42, 0.65)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 99999,
                padding: '20px',
                backdropFilter: 'blur(4px)',
              }}>
                <div style={{
                  background: '#ffffff',
                  borderRadius: '16px',
                  width: '100%',
                  maxWidth: '520px',
                  padding: '26px',
                  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                      🔄 Extend Public Directory Listing
                    </h3>
                    <button
                      type="button"
                      onClick={() => setExtendModal({ isOpen: false, event: null, planId: '', isProcessing: false })}
                      style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
                    >
                      ✕
                    </button>
                  </div>

                  <p style={{ fontSize: '13px', color: '#475569', marginBottom: '16px' }}>
                    Extend directory visibility for <strong>"{extendModal.event.title}"</strong>. The selected duration will be added to your current active expiry date.
                  </p>

                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, color: '#1e293b', marginBottom: '10px' }}>
                    Select Extension Plan:
                  </label>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '22px' }}>
                    {plans.map(p => {
                      const isSel = extendModal.planId === p._id;
                      return (
                        <div
                          key={p._id}
                          onClick={() => setExtendModal(prev => ({ ...prev, planId: p._id }))}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            border: isSel ? '2px solid #7c3aed' : '1px solid #cbd5e1',
                            background: isSel ? '#f5f3ff' : '#ffffff',
                            cursor: 'pointer',
                          }}
                        >
                          <div>
                            <strong style={{ fontSize: '13px', color: '#1e293b' }}>{p.weeks} Week{p.weeks > 1 ? 's' : ''} ({p.name})</strong>
                            <p style={{ margin: '2px 0 0 0', fontSize: '11.5px', color: '#64748b' }}>Adds +{p.durationDays || p.weeks * 7} days to listing</p>
                          </div>
                          <div style={{ fontSize: '16px', fontWeight: 800, color: '#7c3aed' }}>
                            ₹{p.price}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => setExtendModal({ isOpen: false, event: null, planId: '', isProcessing: false })}
                      style={{ padding: '10px 18px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f1f5f9', color: '#475569', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmExtend}
                      disabled={extendModal.isProcessing}
                      style={{
                        padding: '10px 22px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                        color: '#ffffff',
                        border: 'none',
                        fontWeight: 800,
                        fontSize: '13px',
                        cursor: extendModal.isProcessing ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {extendModal.isProcessing ? '⏳ Processing...' : '💳 Pay & Extend Listing'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* REPUBLISH EVENT MODAL */}
            {republishModal.isOpen && republishModal.event && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(15, 23, 42, 0.65)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 99999,
                padding: '20px',
                backdropFilter: 'blur(4px)',
              }}>
                <div style={{
                  background: '#ffffff',
                  borderRadius: '16px',
                  width: '100%',
                  maxWidth: '540px',
                  padding: '26px',
                  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                      🚀 Republish Event to Directory
                    </h3>
                    <button
                      type="button"
                      onClick={() => setRepublishModal({ isOpen: false, event: null, planId: '', scheduledAt: '', endsAt: '', isProcessing: false })}
                      style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
                    >
                      ✕
                    </button>
                  </div>

                  <p style={{ fontSize: '13px', color: '#475569', marginBottom: '14px' }}>
                    Republishing <strong>"{republishModal.event.title}"</strong>. Update session timings if needed and select a new publish duration plan.
                  </p>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>New Start Date & Time *</label>
                    <input
                      type="datetime-local"
                      value={republishModal.scheduledAt}
                      onChange={(e) => setRepublishModal(prev => ({ ...prev, scheduledAt: e.target.value }))}
                      required
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                    />
                    {republishModal.scheduledAt && (
                      <div style={{ marginTop: '6px', fontSize: '12px', color: '#059669', fontWeight: 600 }}>
                        ✨ Event end date will be automatically decided based on the selected plan duration below.
                      </div>
                    )}
                  </div>

                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, color: '#1e293b', marginBottom: '10px' }}>
                    Select Publish Duration Plan:
                  </label>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '22px' }}>
                    {plans.map(p => {
                      const isSel = republishModal.planId === p._id;
                      return (
                        <div
                          key={p._id}
                          onClick={() => setRepublishModal(prev => ({ ...prev, planId: p._id }))}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            border: isSel ? '2px solid #10b981' : '1px solid #cbd5e1',
                            background: isSel ? '#ecfdf5' : '#ffffff',
                            cursor: 'pointer',
                          }}
                        >
                          <div>
                            <strong style={{ fontSize: '13px', color: '#1e293b' }}>{p.weeks} Week{p.weeks > 1 ? 's' : ''} ({p.name})</strong>
                            <p style={{ margin: '2px 0 0 0', fontSize: '11.5px', color: '#64748b' }}>Visible for {p.durationDays || p.weeks * 7} full days</p>
                          </div>
                          <div style={{ fontSize: '16px', fontWeight: 800, color: '#059669' }}>
                            ₹{p.price}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => setRepublishModal({ isOpen: false, event: null, planId: '', scheduledAt: '', endsAt: '', isProcessing: false })}
                      style={{ padding: '10px 18px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f1f5f9', color: '#475569', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmRepublish}
                      disabled={republishModal.isProcessing}
                      style={{
                        padding: '10px 22px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: '#ffffff',
                        border: 'none',
                        fontWeight: 800,
                        fontSize: '13px',
                        cursor: republishModal.isProcessing ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {republishModal.isProcessing ? '⏳ Processing...' : '💳 Pay & Republish Event'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* EDIT EVENT MODAL */}
            {editEventId && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(15, 23, 42, 0.75)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: '16px'
              }}>
                <div style={{
                  width: '650px',
                  maxWidth: '95vw',
                  maxHeight: '90vh',
                  backgroundColor: '#ffffff',
                  borderRadius: '16px',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
                  padding: '24px',
                  overflowY: 'auto'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#0f172a', fontWeight: 800 }}>✏️ Edit Event Details</h3>
                    <button
                      onClick={() => setEditEventId(null)}
                      style={{ border: 'none', background: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b' }}
                    >
                      ✕
                    </button>
                  </div>

                  <form onSubmit={handleUpdateEvent}>
                    <div className="form-group" style={{ marginBottom: '14px' }}>
                      <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>Event Title *</label>
                      <input
                        type="text"
                        value={editEventForm.title}
                        onChange={(e) => setEditEventForm({ ...editEventForm, title: e.target.value })}
                        required
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                      <div className="form-group">
                        <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>Category</label>
                        <select
                          value={editEventForm.eventType}
                          onChange={(e) => setEditEventForm({ ...editEventForm, eventType: e.target.value })}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                        >
                          <option value="seminar">🎤 Seminar</option>
                          <option value="workshop">🛠️ Workshop</option>
                          <option value="event">🎉 Event</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>Mode</label>
                        <select
                          value={editEventForm.mode}
                          onChange={(e) => setEditEventForm({ ...editEventForm, mode: e.target.value })}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                        >
                          <option value="online">💻 Online</option>
                          <option value="offline">🏥 Offline</option>
                        </select>
                      </div>
                    </div>

                    {editEventForm.mode === 'offline' && (
                      <div className="form-group" style={{ marginBottom: '14px' }}>
                        <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>Clinic / Venue Location</label>
                        <input
                          type="text"
                          value={editEventForm.location}
                          onChange={(e) => setEditEventForm({ ...editEventForm, location: e.target.value })}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                        />
                      </div>
                    )}

                    <div className="form-group" style={{ marginBottom: '14px' }}>
                      <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>Description</label>
                      <textarea
                        value={editEventForm.description}
                        onChange={(e) => setEditEventForm({ ...editEventForm, description: e.target.value })}
                        rows="3"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                      ></textarea>
                    </div>

                    {/* Client Purpose: Event Fee and Registration Link */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '14px' }}>
                      <div className="form-group">
                        <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span>🎟️</span> Event Fee (for Clients)
                        </label>
                        <input
                          type="text"
                          value={editEventForm.eventFee}
                          onChange={(e) => setEditEventForm({ ...editEventForm, eventFee: e.target.value })}
                          placeholder="e.g. Free, ₹499, ₹999 / attendee"
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                        />
                      </div>

                      <div className="form-group">
                        <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span>🔗</span> Client Registration Link (URL)
                        </label>
                        <input
                          type="url"
                          value={editEventForm.registrationLink}
                          onChange={(e) => setEditEventForm({ ...editEventForm, registrationLink: e.target.value })}
                          placeholder="https://forms.gle/... or https://zoom.us/..."
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                        />
                      </div>
                    </div>

                    {/* Video / File Upload Section in Edit Modal */}
                    <div style={{ marginBottom: '14px', padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <label style={{ fontWeight: 700, fontSize: '13px', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>🎥</span> Upload Event Video / Media File
                        </label>
                        {uploadingEventVideo && <span style={{ fontSize: '12px', color: '#0284c7', fontWeight: 700 }}>⏳ Uploading...</span>}
                      </div>

                      {eventVideoError && (
                        <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '12px', marginBottom: '10px' }}>
                          ⚠️ {eventVideoError}
                        </div>
                      )}

                      {editEventForm.videoUrl ? (
                        <div style={{ background: '#ffffff', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #a7f3d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '18px' }}>🎬</span>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '12.5px', color: '#065f46' }}>{editEventForm.videoFileName || 'Attached Video'}</div>
                              <div style={{ fontSize: '11px', color: '#64748b' }}>
                                {editEventForm.videoFileSize ? `${(editEventForm.videoFileSize / (1024 * 1024)).toFixed(2)} MB • ` : ''}
                                <a href={editEventForm.videoUrl} target="_blank" rel="noreferrer" style={{ color: '#0284c7', textDecoration: 'underline', fontWeight: 600 }}>
                                  Preview Video File ↗
                                </a>
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveEventVideo(true)}
                            style={{ padding: '4px 8px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                          >
                            ✕ Remove
                          </button>
                        </div>
                      ) : (
                        <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #cbd5e1', borderRadius: '8px', padding: '12px', background: '#ffffff', cursor: uploadingEventVideo ? 'not-allowed' : 'pointer', textAlign: 'center' }}>
                          <input type="file" accept="video/*,.mp4,.mov,.webm,.mkv,.avi,.pdf" onChange={(e) => handleEventVideoUpload(e, true)} disabled={uploadingEventVideo} style={{ display: 'none' }} />
                          <span style={{ fontSize: '20px', marginBottom: '2px' }}>📁</span>
                          <span style={{ fontWeight: 700, color: '#475569', fontSize: '12.5px' }}>{uploadingEventVideo ? 'Uploading...' : 'Click or Drag to Upload Video (.mp4, .mov, etc.)'}</span>
                        </label>
                      )}
                    </div>

                    <div className="form-group" style={{ marginBottom: '16px' }}>
                      <label style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>Start Date & Time *</label>
                      <input
                        type="datetime-local"
                        value={editEventForm.scheduledAt}
                        onChange={(e) => setEditEventForm({ ...editEventForm, scheduledAt: e.target.value })}
                        required
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px' }}
                      />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                      <button
                        type="button"
                        onClick={() => setEditEventId(null)}
                        style={{ padding: '9px 18px', borderRadius: '20px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 600, cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        style={{ padding: '9px 22px', borderRadius: '20px', border: 'none', background: '#0284c7', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                      >
                        💾 Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Events Grid List */}
            {events.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '18px' }}>
                {events.map(event => {
                  const startDate = event.scheduledAt ? new Date(event.scheduledAt).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD';
                  const startTime = event.scheduledAt ? new Date(event.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                  const endTime = event.endsAt ? new Date(event.endsAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

                  const statusColor = event.status === 'approved' ? { bg: '#dcfce7', text: '#15803d', border: '#86efac' }
                    : event.status === 'rejected' ? { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' }
                      : { bg: '#fef3c7', text: '#b45309', border: '#fde68a' };

                  const expiryInfo = formatExpiryDisplay(event);

                  return (
                    <div key={event._id} style={{
                      backgroundColor: '#ffffff',
                      borderRadius: '14px',
                      border: '1px solid #e2e8f0',
                      padding: '18px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: '12px'
                    }}>
                      <div>
                        {/* Header Badges */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '3px 10px',
                            borderRadius: '12px',
                            backgroundColor: event.eventType === 'workshop' ? '#f0fdf4' : event.eventType === 'event' ? '#faf5ff' : '#eff6ff',
                            color: event.eventType === 'workshop' ? '#166534' : event.eventType === 'event' ? '#6b21a8' : '#1e40af',
                            border: '1px solid ' + (event.eventType === 'workshop' ? '#bbf7d0' : event.eventType === 'event' ? '#e9d5ff' : '#bfdbfe'),
                            textTransform: 'uppercase'
                          }}>
                            {event.eventType}
                          </span>

                          <span style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '3px 10px',
                            borderRadius: '12px',
                            backgroundColor: statusColor.bg,
                            color: statusColor.text,
                            border: `1px solid ${statusColor.border}`,
                            textTransform: 'capitalize'
                          }}>
                            {event.status === 'approved' ? '✓ Approved' : event.status === 'rejected' ? '✕ Rejected' : '⏳ Pending Approval'}
                          </span>
                        </div>

                        {/* Directory Visibility Status Banner */}
                        {expiryInfo && (
                          <div style={{
                            padding: '6px 10px',
                            borderRadius: '8px',
                            marginBottom: '10px',
                            fontSize: '11.5px',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: expiryInfo.isExpired ? '#fef2f2' : '#f0fdf4',
                            border: `1px solid ${expiryInfo.isExpired ? '#fecaca' : '#bbf7d0'}`,
                            color: expiryInfo.isExpired ? '#b91c1c' : '#166534',
                          }}>
                            <span>{expiryInfo.isExpired ? '🔴 Expired from Directory:' : '🟢 Active in Directory:'}</span>
                            <span>{expiryInfo.text}</span>
                          </div>
                        )}

                        <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#0f172a', fontWeight: 700, lineHeight: 1.3 }}>
                          {event.title}
                        </h4>

                        <div style={{ fontSize: '12.5px', color: '#475569', display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' }}>
                          <div>📅 <strong>Date:</strong> {startDate}</div>
                          {startTime && <div>🕐 <strong>Time:</strong> {startTime} {endTime ? `- ${endTime}` : ''}</div>}
                          <div>
                            {event.mode === 'online' ? (
                              <span style={{ color: '#2563eb', fontWeight: 600 }}>💻 Mode: Online Meeting</span>
                            ) : (
                              <span style={{ color: '#059669', fontWeight: 600 }}>🏥 Location: {event.location || 'Clinic'}</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              background: '#fef3c7',
                              color: '#92400e',
                              borderRadius: '6px',
                              fontSize: '11.5px',
                              fontWeight: 700,
                              border: '1px solid #fde68a'
                            }}>
                              🎟️ Fee: {event.eventFee || 'Free'}
                            </span>
                          </div>
                          {event.registrationLink && (
                            <div style={{ marginTop: '2px' }}>
                              <a
                                href={event.registrationLink}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  color: '#4338ca',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  textDecoration: 'underline'
                                }}
                              >
                                🔗 Client Registration Link ↗
                              </a>
                            </div>
                          )}
                          {event.isFirstFreeEvent && (
                            <div style={{ color: '#d97706', fontWeight: 700, fontSize: '11.5px' }}>
                              🎁 1st Free Event for Pro Member
                            </div>
                          )}
                        </div>

                        {event.description && (
                          <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: '1.4', background: '#f8fafc', padding: '8px 12px', borderRadius: '8px' }}>
                            {event.description}
                          </p>
                        )}

                        {event.videoUrl && (
                          <div style={{ marginTop: '10px' }}>
                            <a
                              href={event.videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '12px',
                                color: '#7c3aed',
                                fontWeight: 700,
                                textDecoration: 'none',
                                background: '#f5f3ff',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                border: '1px solid #ddd6fe',
                              }}
                            >
                              🎬 {event.videoFileName ? `Video: ${event.videoFileName}` : 'Watch Event Video ↗'}
                            </a>
                          </div>
                        )}

                        {event.mode === 'online' && event.meetingLink && (
                          <div style={{ marginTop: '10px' }}>
                            <a
                              href={event.meetingLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '12px',
                                color: '#2563eb',
                                fontWeight: 700,
                                textDecoration: 'none',
                                background: '#eff6ff',
                                padding: '4px 10px',
                                borderRadius: '6px'
                              }}
                            >
                              🎥 Open Meeting Link
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Action buttons: Dynamic Extend vs Republish */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                        {expiryInfo?.isExpired ? (
                          <button
                            type="button"
                            onClick={() => handleOpenRepublishModal(event)}
                            style={{
                              width: '100%',
                              padding: '8px 14px',
                              borderRadius: '8px',
                              border: 'none',
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: '#ffffff',
                              fontWeight: 700,
                              fontSize: '12.5px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              boxShadow: '0 2px 6px rgba(16, 185, 129, 0.25)',
                            }}
                          >
                            🚀 Republish Event (with Pay)
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleOpenExtendModal(event)}
                            style={{
                              width: '100%',
                              padding: '8px 14px',
                              borderRadius: '8px',
                              border: 'none',
                              background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                              color: '#ffffff',
                              fontWeight: 700,
                              fontSize: '12.5px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              boxShadow: '0 2px 6px rgba(124, 58, 237, 0.25)',
                            }}
                          >
                            🔄 Extend Listing (with Pay)
                          </button>
                        )}

                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleStartEditEvent(event)}
                            style={{
                              flex: 1,
                              padding: '7px 12px',
                              borderRadius: '8px',
                              border: '1px solid #cbd5e1',
                              background: '#ffffff',
                              color: '#0f172a',
                              fontWeight: 600,
                              fontSize: '12.5px',
                              cursor: 'pointer'
                            }}
                          >
                            ✏️ Edit
                          </button>

                          <button
                            onClick={() => handleDeleteEvent(event._id)}
                            style={{
                              flex: 1,
                              padding: '7px 12px',
                              borderRadius: '8px',
                              border: '1px solid #fca5a5',
                              background: '#fef2f2',
                              color: '#dc2626',
                              fontWeight: 600,
                              fontSize: '12.5px',
                              cursor: 'pointer'
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8fafc', borderRadius: '12px', color: '#64748b' }}>
                <span style={{ fontSize: '42px', display: 'block', marginBottom: '10px' }}>🎉</span>
                <h4 style={{ margin: '0 0 6px 0', color: '#0f172a', fontSize: '16px' }}>No events or seminars hosted yet</h4>
                <p style={{ margin: 0, fontSize: '13.5px' }}>Click "Host New Event" above to create and publish your first seminar or workshop.</p>
              </div>
            )}
          </div>
        );
      }
      case 'whatsapp':
        return (
          <div className="physio-dashboard__card fade-in">
            <div className="section-header" style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#111827' }}>💬 Direct WhatsApp Patient Chat</h3>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '4px 0 0 0' }}>Instant 1-click WhatsApp messaging for all patients who booked sessions with you</p>
            </div>

            {bookings.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
                {bookings.map(booking => {
                  const clientName = booking.clientId?.name || booking.clientName || 'Patient';
                  const clientPhone = booking.clientId?.phone || booking.clientPhone || '';
                  const clientEmail = booking.clientId?.email || 'N/A';
                  const dateStr = booking.scheduledAt ? new Date(booking.scheduledAt).toLocaleDateString() : 'N/A';

                  return (
                    <div key={booking._id} style={{ padding: '20px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: '#111827', fontSize: '16px', fontWeight: 700 }}>👤 {clientName}</h4>
                      <p style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#4b5563' }}>📧 Email: {clientEmail}</p>
                      <p style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#4b5563' }}>📞 Phone: {clientPhone || 'N/A'}</p>
                      <p style={{ margin: '0 0 14px 0', fontSize: '13px', color: '#6b7280' }}>📅 Session: {dateStr} ({booking.mode === 'online' ? '💻 Online' : '🏥 In-Person'})</p>

                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            setWhatsappTarget({
                              _id: booking.clientId?._id || booking.clientId,
                              name: clientName,
                              phone: clientPhone,
                              email: clientEmail,
                              role: 'client'
                            });
                            setInitialWhatsappMessage('');
                            setWhatsappModalOpen(true);
                          }}
                          style={{
                            background: '#00a884',
                            color: '#fff',
                            padding: '10px 18px',
                            borderRadius: '20px',
                            fontWeight: 700,
                            fontSize: '13px',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 3px 8px rgba(0,168,132,0.3)'
                          }}
                        >
                          💬 Inbuilt WhatsApp Chat
                        </button>

                        <button
                          onClick={() => handleDownloadChatTranscript(booking.clientId?._id || booking.clientId, clientName)}
                          style={{
                            background: '#ffffff',
                            color: '#0f172a',
                            padding: '10px 16px',
                            borderRadius: '20px',
                            fontWeight: 700,
                            fontSize: '13px',
                            border: '1px solid #cbd5e1',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                          }}
                        >
                          📥 Download Chat
                        </button>

                        <button
                          onClick={() => handleSendChatToClient(booking.clientId?._id || booking.clientId, clientName)}
                          style={{
                            background: '#0284c7',
                            color: '#ffffff',
                            padding: '10px 16px',
                            borderRadius: '20px',
                            fontWeight: 700,
                            fontSize: '13px',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 2px 6px rgba(2,132,199,0.3)'
                          }}
                        >
                          📩 Send Chat to Client
                        </button>

                        {booking.status !== 'completed' && booking.status !== 'cancelled' && (
                          <button
                            onClick={() => handleEndSession(booking)}
                            style={{
                              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                              color: '#ffffff',
                              padding: '10px 18px',
                              borderRadius: '20px',
                              fontWeight: 700,
                              fontSize: '13px',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              boxShadow: '0 3px 8px rgba(239,68,68,0.35)'
                            }}
                          >
                            🏁 End Session
                          </button>
                        )}

                        {clientPhone && (
                          <a
                            href={buildWhatsAppLink(clientPhone, `Hello ${clientName}, I am reaching out regarding your therapy session booked on PsychMitra for ${dateStr}.`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              background: '#25D366',
                              color: '#fff',
                              padding: '8px 16px',
                              borderRadius: '20px',
                              fontWeight: 700,
                              fontSize: '13px',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              boxShadow: '0 3px 8px rgba(37,211,102,0.3)'
                            }}
                          >
                            📲 External App
                          </a>
                        )}

                        {booking.status === 'pending' && (
                          <>
                            <button className="btn-success" onClick={() => updateBooking(booking._id, 'confirmed')}>Accept</button>
                            <button className="btn-danger" onClick={() => updateBooking(booking._id, 'cancelled')}>Decline</button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-data" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                No patient bookings found to start WhatsApp conversations.
              </div>
            )}
          </div>
        );
      case 'worksheets': {
        const filtered = worksheets.filter(w => {
          const matchSearch = !worksheetSearch ||
            w.title.toLowerCase().includes(worksheetSearch.toLowerCase()) ||
            (w.description && w.description.toLowerCase().includes(worksheetSearch.toLowerCase())) ||
            (w.category && w.category.toLowerCase().includes(worksheetSearch.toLowerCase()));
          const matchCat = worksheetCategory === 'all' || w.category === worksheetCategory;
          return matchSearch && matchCat;
        });

        const categories = ['all', ...new Set(worksheets.map(w => w.category).filter(Boolean))];

        return (
          <div className="physio-dashboard__card fade-in">
            <div className="section-header" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px' }}>📑 Clinical Worksheets & Assessment Tools</h3>
                <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
                  Verified worksheets provided by Psychmitra Admin for use during client therapy sessions.
                </p>
              </div>
              <div style={{ background: '#e0e7ff', color: '#4338ca', padding: '6px 14px', borderRadius: '20px', fontWeight: 700, fontSize: '13px' }}>
                {worksheets.length} Tools Available
              </div>
            </div>

            {/* Search & Filter Bar */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
              <div style={{ flex: 1, minWidth: '220px', position: 'relative' }}>
                <input
                  type="text"
                  placeholder="🔍 Search worksheets by keyword, condition, or technique..."
                  value={worksheetSearch}
                  onChange={(e) => setWorksheetSearch(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              <select
                value={worksheetCategory}
                onChange={(e) => setWorksheetCategory(e.target.value)}
                style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', background: '#fff', color: '#333' }}
              >
                <option value="all">All Clinical Categories</option>
                {categories.filter(c => c !== 'all').map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Worksheets Grid */}
            {filtered.length === 0 ? (
              <div className="no-data" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                {worksheets.length === 0 ? 'Loading clinical worksheets library...' : 'No worksheets match your search filter.'}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '18px' }}>
                {filtered.map(w => (
                  <div
                    key={w._id || w.id}
                    style={{
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '18px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                    }}
                  >
                    <div>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#1e293b', fontWeight: 700, lineHeight: 1.4 }}>
                        {w.title}
                      </h4>
                    </div>

                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '12px', marginTop: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94a3b8', marginBottom: '12px' }}>
                        <span>Format: {(w.fileType || 'PDF').toUpperCase()}</span>
                        <span>📥 {w.downloadCount || 0} Downloads</span>
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => setSelectedDashboardWorksheet(w)}
                          style={{
                            flex: 1,
                            padding: '8px',
                            background: '#f8fafc',
                            color: '#334155',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          👁️ View
                        </button>
                        <button
                          onClick={() => handleDashboardWorksheetDownload(w)}
                          disabled={downloadingWorksheetId === (w._id || w.id)}
                          style={{
                            flex: 1.2,
                            padding: '8px 12px',
                            background: '#4f46e5',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                          }}
                        >
                          <span>{downloadingWorksheetId === (w._id || w.id) ? '⏳' : '📥'}</span>
                          <span>Download</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Selected Detail Modal */}
            {selectedDashboardWorksheet && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 9999,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px',
                }}
              >
                <div style={{ background: '#fff', borderRadius: '14px', maxWidth: '500px', width: '100%', padding: '24px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#0f172a' }}>{selectedDashboardWorksheet.title}</h3>
                    <button onClick={() => setSelectedDashboardWorksheet(null)} style={{ background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
                  </div>

                  <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', marginBottom: '18px', fontSize: '13px', display: 'flex', justifyContent: 'space-between' }}>
                    <span><strong>Format:</strong> {(selectedDashboardWorksheet.fileType || 'PDF').toUpperCase()}</span>
                    <span><strong>Downloads:</strong> {selectedDashboardWorksheet.downloadCount || 0}</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button onClick={() => setSelectedDashboardWorksheet(null)} style={{ padding: '8px 16px', background: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>Close</button>
                    <button
                      onClick={() => {
                        handleDashboardWorksheetDownload(selectedDashboardWorksheet);
                        setSelectedDashboardWorksheet(null);
                      }}
                      style={{ padding: '8px 18px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' }}
                    >
                      📥 Download Tool
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }
      case 'community':
        return <TherapistCommunityPostManager />;
      default:
        return null;
    }
  };

  // Dynamic patient rating and reviews count calculation
  const ratedBookings = (bookings || []).filter((b) => b.rating && Number(b.rating) > 0);
  const totalRatingSum = ratedBookings.reduce((sum, b) => sum + Number(b.rating), 0);
  const calculatedRating =
    ratedBookings.length > 0
      ? (totalRatingSum / ratedBookings.length).toFixed(1)
      : profile?.rating && Number(profile.rating) > 0
      ? Number(profile.rating).toFixed(1)
      : profile?.averageRating && Number(profile.averageRating) > 0
      ? Number(profile.averageRating).toFixed(1)
      : '5.0';

  const dynamicRatingCount =
    ratedBookings.length > 0
      ? ratedBookings.length
      : profile?.reviewCount || profile?.reviewsCount || (Array.isArray(profile?.reviews) ? profile.reviews.length : 0);

  if (!authSession?.token) {
    return null;
  }

  return (
    <div className="physio-panel-layout">
      {/* Sidebar */}
      <aside className="physio-sidebar">
        <div className="sidebar-header">
          <span className="sidebar-logo">🩺</span>
          <span className="sidebar-title">MentalUP<br /><small>Practitioner Portal</small></span>
        </div>

        <nav className="sidebar-nav">
          {PHYSIO_TABS.map(tab => (
            <button
              key={tab.key}
              className={`sidebar-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="physio-user-info">
            <div className="physio-user-avatar">
              {(user?.name || 'Practitioner').charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="physio-user-name">{user?.name || 'Practitioner'}</div>
              <div className="physio-user-role" style={{ fontSize: '11px', opacity: 0.8 }}>
                {user?.email || profile?.specialization || 'Mental Health Professional'}
              </div>
            </div>
          </div>
          <div className="sidebar-footer-actions">
            <button className="home-btn" onClick={() => window.location.href = '/'}>
              🏠 Return to Home
            </button>
            <button className="logout-btn" onClick={() => {
              clearAuthSession();
              window.location.href = '/login';
            }}>
              🚪 Secure Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="physio-main-content">
        <div className="dashboard-page-content physio-theme">
          {/* Welcome Section & Stats Cards - Kept ONLY on Dashboard overview tab */}
          {activeTab === 'dashboard' && (
            <>
              <div className="welcome-banner">
                <div className="welcome-content">
                  <h2>Welcome to your Dashboard, {user?.name || 'Practitioner'}! 👋</h2>
                  <p>Manage your patient sessions, update your profile, and upload verification credentials.</p>
                </div>
                <div className="welcome-badges">
                  <TherapistNotifications />
                  <span className={`verification-badge ${profile?.verificationStatus || 'pending'}`}>
                    {profile?.verificationStatus === 'approved' ? '✓ Verified Practitioner' : profile?.verificationStatus === 'rejected' ? '✗ Verification Rejected' : '⏳ Verification Pending'}
                  </span>
                  <span className="pro-badge">🧬 {profile?.specialization || 'Mental Health Practitioner'}</span>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="dashboard-page-stats">
                <div className="dashboard-page-stat-card">
                  <div className="stat-card-icon blue">📅</div>
                  <div className="stat-card-info">
                    <h4>{bookings.length}</h4>
                    <p>Total Bookings</p>
                  </div>
                </div>
                <div className="dashboard-page-stat-card">
                  <div className="stat-card-icon purple">👥</div>
                  <div className="stat-card-info">
                    <h4>{bookings.filter(b => b.status === 'confirmed').length}</h4>
                    <p>Confirmed Patients</p>
                  </div>
                </div>
                <div className="dashboard-page-stat-card">
                  <div className="stat-card-icon green">📝</div>
                  <div className="stat-card-info">
                    <h4>{blogs.length}</h4>
                    <p>Published Articles</p>
                  </div>
                </div>
                <div className="dashboard-page-stat-card">
                  <div className="stat-card-icon orange">⭐</div>
                  <div className="stat-card-info">
                    <h4>
                      {calculatedRating}{' '}
                      <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                        ({dynamicRatingCount} {dynamicRatingCount === 1 ? 'review' : 'reviews'})
                      </span>
                    </h4>
                    <p>Patient Rating</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {statusMessage && <div className="status-message success-message">{statusMessage}</div>}
          {errorMessage && <div className="status-message error-message">{errorMessage}</div>}

          {/* Tab Content Area */}
          <div className="physio-dashboard__tab-content">
            {renderTabContent()}
          </div>
        </div>
      </main>

      <InbuiltWhatsAppModal
        isOpen={whatsappModalOpen}
        onClose={() => setWhatsappModalOpen(false)}
        targetUser={whatsappTarget}
        initialMessage={initialWhatsappMessage}
        contactsList={bookings.map(b => {
          const client = b.clientId && typeof b.clientId === 'object' ? b.clientId : {};
          return {
            _id: client._id || b.clientId,
            name: client.name || b.clientName || 'Patient',
            phone: client.phone || b.clientPhone || '',
            email: client.email || b.clientEmail || '',
            role: 'client',
            status: b.status,
            isSessionCompleted: b.status === 'completed'
          };
        })}
      />

      <BookingActionModal
        isOpen={bookingActionModalOpen}
        onClose={() => setBookingActionModalOpen(false)}
        booking={bookingActionModalTarget}
        actionType={bookingActionModalType}
        userRole="therapist"
        onSuccess={async () => {
          const refreshed = await bookingApi.mine();
          setBookings(refreshed.bookings || []);
        }}
      />
    </div>
  );
}

export default TherapistDashboardNew;
